<template>
  <v-col>
    <!-- Compact Header Section -->
    <div class="time-view-header-compact mb-3">
      <!-- Main Header Bar with Tabs -->
      <div class="tabs-section">
        <v-toolbar
          flat
          color="white"
          class="header-toolbar px-0 py-0"
          height="56"
        >
          <!-- Title Icon -->
          <div class="header-icon-wrapper mr-3">
            <font-awesome-icon
              :icon="['fas', 'clock']"
              size="lg"
              class="primary--text"
            />
          </div>

          <!-- Inline Tabs -->
          <v-tabs
            v-model="activeTab"
            color="primary"
            slider-color="primary"
            show-arrows
            class="time-view-tabs-inline flex-grow-1"
            height="56"
          >
            <!-- Custom Font Awesome icons for navigation arrows -->
            <template v-slot:prev>
              <font-awesome-icon
                :icon="['fas', 'chevron-left']"
                size="xs"
                class="tab-nav-arrow"
                focusable="false"
                style="height: 16px !important; width: 16px !important;"
              />
            </template>

            <template v-slot:next>
              <font-awesome-icon
                :icon="['fas', 'chevron-right']"
                size="xs"
                class="tab-nav-arrow"
                focusable="false"
                style="height: 16px !important; width: 16px !important;"
              />
            </template>

            <v-tab
              v-for="tab in tabsWithCounts"
              :key="tab.id"
              class="tab-compact"
              @click="setShowTimeEntryList(tab.id)"
            >
              <font-awesome-icon
                :icon="tab.icon"
                class="mr-2"
                size="sm"
              />
              {{ tab.label }}
              <v-chip
                v-if="tab.count !== undefined"
                x-small
                outlined
                class="ml-2 tab-count-chip"
              >
                {{ tab.count }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-spacer />

          <!-- Action Buttons -->
          <div class="header-actions d-flex align-center">
            <!-- Templates Menu (always visible) -->
            <v-menu
              v-if="entryTemplates.length > 0"
              offset-y
              left
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-badge
                    :content="entryTemplates.length"
                    color="primary"
                    overlap
                    offset-x="10"
                    offset-y="10"
                  >
                    <font-awesome-icon :icon="['fas', 'list']" />
                  </v-badge>
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  v-for="template in entryTemplates"
                  :key="template.id"
                  @click="applyTemplate(template)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ template.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ template.clientName }} • {{ template.timeClassificationName }} • {{ durationFormatter(template.duration) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click.stop="deleteTemplate(template.id)"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'trash']"
                        size="sm"
                      />
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Conditional: Show Bulk Actions OR Add Entry Button -->
            <div class="action-buttons-container justify-end">
              <!-- Bulk Actions (shown when items are selected) -->
              <v-slide-x-reverse-transition mode="out-in">
                <div
                  v-if="enableBulkOperations && selectedEntries.length > 0 && showTimeEntryList === 'time-entry'"
                  key="bulk-actions"
                  class="d-flex align-center"
                >
                  <v-chip
                    color="primary"
                    text-color="white"
                    small
                    close
                    class="mr-2 font-weight-bold"
                    @click:close="clearSelection"
                  >
                    {{ selectedEntries.length }} selected
                  </v-chip>

                  <v-btn
                    color="primary"
                    outlined
                    small
                    class="mr-2"
                    @click="openBulkCopyDialog"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'copy']"
                      size="sm"
                      class="mr-1"
                    />
                    Copy
                  </v-btn>

                  <v-btn
                    color="error"
                    outlined
                    small
                    @click="openBulkDeleteDialog"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'trash']"
                      size="sm"
                      class="mr-1"
                    />
                    Delete
                  </v-btn>
                </div>

                <!-- Add Entry Button (shown when no items selected or when bulk operations disabled, hidden on mobile) -->
                <v-btn
                  v-if="!enableBulkOperations || selectedEntries.length === 0"
                  key="add-entry"
                  color="primary"
                  depressed
                  class="add-time-btn-compact mr-2 d-none d-md-flex"
                  @click="openTimeEntryFormCreate(today)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'plus']"
                    size="sm"
                  />
                  <span class="ml-2">Add Entry</span>
                </v-btn>
              </v-slide-x-reverse-transition>
            </div>
          </div>
        </v-toolbar>

        <v-divider />

        <!-- Compact Controls Bar -->
        <div class="controls-bar">
          <v-row class="align-center ma-0 pa-3 pl-2">
            <!-- Search Field -->
            <v-col
              cols="12"
              sm="6"
              md="5"
              lg="4"
              class="py-1"
            >
              <v-text-field
                v-model="tableSearch"
                placeholder="Search entries..."
                single-line
                hide-details
                dense
                outlined
                clearable
                class="search-field-compact"
              >
                <template v-slot:prepend-inner>
                  <font-awesome-icon
                    :icon="['fas', 'magnifying-glass']"
                    size="sm"
                    class="search-icon mt-1"
                  />
                </template>
              </v-text-field>
            </v-col>

            <!-- Filter Button -->
            <v-col
              cols="auto"
              class="py-1 d-flex align-center"
            >
              <v-btn
                text
                outlined
                color="primary"
                :class="{ 'filter-active': hasActiveFilters }"
                class="filter-btn-compact"
                @click="filterMenuOpen = !filterMenuOpen"
              >
                <font-awesome-icon
                  :icon="['fas', 'filter']"
                  size="sm"
                  class="mr-1"
                />

                <span>Filters</span>

                <v-chip
                  v-if="hasActiveFilters"
                  x-small
                  color="primary"
                  class="ml-2"
                >
                  {{ activeFilterCount }}
                </v-chip>
              </v-btn>
            </v-col>

            <!-- Column Picker Button (only in table view) -->
            <v-col
              v-if="showTimeEntryList !== 'time-entry' && listViewMode === 'table'"
              cols="auto"
              class="py-1"
            >
              <v-dialog
                v-model="columnPickerDialog"
                max-width="350"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    outlined
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'sliders']"
                      size="sm"
                      class="mr-1"
                    />
                    <span>Columns</span>
                  </v-btn>
                </template>

                <column-picker
                  :current-required-columns="currentRequiredColumns"
                  :report-key="reportKey"
                  :table-columns="tableColumns"
                />
              </v-dialog>
            </v-col>

            <!-- Export Button (only in table view) -->
            <v-col
              v-if="showTimeEntryList !== 'time-entry' && listViewMode === 'table'"
              cols="auto"
              class="py-1"
            >
              <v-menu
                offset-y
                nudge-bottom="6"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    outlined
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'circle-down']"
                      size="sm"
                      class="mr-1"
                    />
                    <span>Export</span>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item @click="exportTableData('csv')">
                    <v-list-item-title>CSV</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportTableData('xlsx')">
                    <v-list-item-title>XLSX</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>

            <!-- View Mode Toggle (only shown in time-entry view) -->
            <v-col
              v-if="showTimeEntryList === 'time-entry'"
              cols="auto"
              class="py-1"
            >
              <v-btn-toggle
                v-model="weekViewMode"
                mandatory
                dense
                borderless
                color="primary"
              >
                <v-btn
                  value="columns"
                  small
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <font-awesome-icon
                        :icon="['fas', 'calendar-days']"
                        focusable="false"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>Calendar</span>
                  </v-tooltip>
                </v-btn>
                <v-btn
                  value="calendar"
                  small
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <font-awesome-icon
                        :icon="['fas', 'calendar-week']"
                        focusable="false"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>List</span>
                  </v-tooltip>
                </v-btn>
              </v-btn-toggle>
            </v-col>

            <!-- List View Mode Toggle (for MTD, Billable, Projects tabs) -->
            <v-col
              v-if="showTimeEntryList !== 'time-entry'"
              cols="auto"
              class="py-1"
            >
              <v-btn-toggle
                v-model="listViewMode"
                mandatory
                dense
                borderless
                color="primary"
              >
                <v-btn
                  value="cards"
                  small
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <font-awesome-icon
                        :icon="['fas', 'grip']"
                        focusable="false"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>Cards</span>
                  </v-tooltip>
                </v-btn>
                <v-btn
                  value="table"
                  small
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <font-awesome-icon
                        :icon="['fas', 'table']"
                        focusable="false"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>Table</span>
                  </v-tooltip>
                </v-btn>
              </v-btn-toggle>
            </v-col>

            <v-spacer class="d-none d-sm-flex" />

            <!-- Date and Navigation Controls -->
            <v-col
              cols="12"
              sm="auto"
              class="py-1 d-flex align-center justify-end"
            >
              <!-- Time Shift Navigation (Time Entry view only) -->
              <template v-if="showTimeEntryList === 'time-entry'">
                <v-btn
                  icon
                  small
                  color="primary"
                  class="mr-1"
                  @click="timeShiftLeft"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <font-awesome-icon
                        style="outline-color: transparent;"
                        :icon="['fas', 'chevron-left']"
                        focusable="false"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>Previous Week</span>
                  </v-tooltip>
                </v-btn>

                <!-- Week Number Display -->
                <v-chip
                  small
                  outlined
                  color="primary"
                  class="mx-1 font-weight-medium"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                      >
                        W{{ currentWeekNumber }}
                      </span>
                    </template>
                    <span>Week {{ currentWeekNumber }} of {{ currentYear }}</span>
                  </v-tooltip>
                </v-chip>

                <v-btn
                  icon
                  small
                  color="primary"
                  class="ml-1 mr-2"
                  @click="timeShiftRight"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <font-awesome-icon
                        style="outline-color: transparent;"
                        :icon="['fas', 'chevron-right']"
                        focusable="false"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <span>Next Week</span>
                  </v-tooltip>
                </v-btn>
              </template>

              <!-- Date Range Picker (all views) -->
              <date-range-picker-button
                :loading="applyingDateFilter"
                :initial-start-date="currentDateRange[0]"
                :initial-end-date="currentDateRange[1]"
                @apply-date-range="onApplyDateRange"
                @clear-date-range="onClearDateRange"
              />
            </v-col>
          </v-row>

          <!-- Expandable Filter Section -->
          <v-expand-transition>
            <div v-if="filterMenuOpen">
              <v-divider class="mt-0" />

              <v-row class="ma-0 pa-2 pb-0">
                <!-- Person Filter (only shown if multiple persons have entries) -->
                <v-col
                  v-if="shouldShowPersonFilter"
                  class="col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3"
                >
                  <v-select
                    v-model="selectedPersonFilter"
                    :items="personFilterOptions"
                    label="Person"
                    outlined
                    hide-details
                    clearable
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    class="filter-select"
                  >
                    <template v-slot:prepend-inner>
                      <font-awesome-icon
                        :icon="['fas', 'user']"
                        class="filter-icon"
                      />
                    </template>
                  </v-select>
                </v-col>

                <v-col :class="shouldShowPersonFilter ? 'col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3' : 'col-12 col-sm-6 col-md-4 col-lg-4 col-xl-4'">
                  <v-select
                    v-model="selectedClientFilter"
                    :items="clientFilterOptions"
                    label="Client"
                    outlined
                    hide-details
                    clearable
                    class="filter-select"
                  >
                    <template v-slot:prepend-inner>
                      <font-awesome-icon
                        :icon="['fas', 'building']"
                        class="filter-icon"
                      />
                    </template>
                  </v-select>
                </v-col>

                <v-col :class="shouldShowPersonFilter ? 'col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3' : 'col-12 col-sm-6 col-md-4 col-lg-4 col-xl-4'">
                  <v-select
                    v-model="selectedTimeTypeFilter"
                    :items="timeTypeFilterOptions"
                    label="Time Type"
                    outlined
                    hide-details
                    clearable
                    class="filter-select"
                  >
                    <template v-slot:prepend-inner>
                      <font-awesome-icon
                        :icon="['fas', 'clock']"
                        class="filter-icon"
                      />
                    </template>
                  </v-select>
                </v-col>

                <v-col :class="shouldShowPersonFilter ? 'col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3' : 'col-12 col-sm-6 col-md-4 col-lg-4 col-xl-4'">
                  <v-select
                    v-model="selectedProjectFilter"
                    :items="projectFilterOptions"
                    label="Project"
                    outlined
                    hide-details
                    clearable
                    class="filter-select"
                  >
                    <template v-slot:prepend-inner>
                      <font-awesome-icon
                        :icon="['fas', 'folder']"
                        class="filter-icon"
                      />
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <v-row
                v-if="hasActiveFilters"
                class="ma-0 pa-3 pt-0"
                justify="end"
              >
                <v-col cols="auto">
                  <v-btn
                    text
                    small
                    color="primary"
                    @click="clearAllFilters"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'times-circle']"
                      size="sm"
                      class="mr-1"
                    />
                    Clear All
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </div>

    <!-- Compact Stacked Time Breakdown -->
    <v-row
      v-if="showSummaries && showTimeEntryList === 'time-entry'"
      class="mt-2 mb-3"
    >
      <v-col>
        <v-card
          outlined
          class="time-breakdown-card pa-3"
        >
          <v-row class="align-center mb-1">
            <v-col
              cols="12"
              class="pb-0"
            >
              <span class="text-subtitle-2 font-weight-bold time-breakdown-title">
                Time Breakdown
              </span>
            </v-col>
          </v-row>

          <v-row class="align-center mb-0">
            <v-col
              cols="12"
              class="pt-1"
            >
              <div class="d-flex align-center legend-container-inline">
                <!-- Total Hours Display -->
                <v-chip
                  color="primary"
                  text-color="white"
                  small
                  class="px-3 font-weight-bold mr-2 mb-1"
                >
                  <font-awesome-icon
                    :icon="['fas', 'clock']"
                    class="mr-2"
                    size="sm"
                  />

                  <span>{{ durationFormatter(currentWeek.total) }} Hrs</span>

                  <span class="ml-1 font-weight-medium">Total</span>
                </v-chip>

                <v-spacer />

                <!-- Breakdown Chips -->
                <div class="d-flex flex-wrap justify-end">
                  <v-chip
                    v-for="segment in progressSegments"
                    :key="segment.id"
                    x-small
                    :class="`mr-1 mb-1 legend-chip ${segment.colorClass}`"
                    text-color="white"
                  >
                    <span class="mr-1 font-weight-medium">{{ segment.label }}</span>

                    <span class="font-weight-bold">{{ durationFormatter(segment.value) }}</span>
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Stacked Progress Bar -->
          <div class="stacked-progress-container mb-2">
            <div class="stacked-progress-bar">
              <v-tooltip
                v-for="segment in progressSegments"
                :key="segment.id"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    :class="`progress-segment ${segment.colorClass}`"
                    :style="`width: ${segment.percentage}%`"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <span>
                  <strong>{{ segment.label }}:</strong> {{ durationFormatter(segment.value) }}
                  ({{ segment.percentage.toFixed(1) }}%)
                </span>
              </v-tooltip>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row
      wrap
      class="justify-center mt-3"
    >
      <v-col
        class="pt-0 px-4"
      >
        <!-- Time Entry View -->
        <v-row
          v-if="showTimeEntryList === 'time-entry'"
          class="justify-center"
        >
          <v-col>
            <v-card
              v-if="currentWeek && currentWeek.week"
              class="week-container"
              outlined
            >
              <v-card-actions class="px-6 pt-4">
                <v-col class="pa-0">
                  <!-- Locked Week Notices -->
                  <v-card
                    v-if="week.length && lockedDates.length === week.length"
                    color="warning"
                    outlined
                    class="mb-3"
                  >
                    <v-card-text>
                      This week is out of range for edits.
                      Message an administrator to request edits to time
                      entries outside the allowed date range.
                    </v-card-text>
                  </v-card>

                  <!-- Partial Locked Week Notice -->
                  <v-card
                    v-else-if="week.length && lockedDates.length"
                    color="warning"
                    outlined
                    class="mb-3"
                  >
                    <v-card-text>
                      Some dates in this week are out of range.
                      Message an administrator to request edits to time
                      entries outside the allowed date range.
                    </v-card-text>
                  </v-card>

                  <!-- CALENDAR VIEW (Option 1) - Day-grouped layout -->
                  <div v-if="weekViewMode === 'calendar'">
                    <!-- Daily Entries -->
                    <v-card
                      v-for="(weekdate, index) in currentWeek.week"
                      :key="index"
                      :class="`mt-3 py-2 ${setEntryClass(weekdate, index)}`"
                      outlined
                    >
                      <v-container
                        class="pa-0 ma-0"
                      >
                        <!-- WeekDate's Header -->
                        <v-card-title
                          primary-title
                          class="py-2 px-3"
                        >
                          <v-row class="align-center ma-0">
                            <v-col
                              cols="auto"
                              class="pa-0"
                            >
                              <div :class="`subtitle-2 font-weight-bold ${lockedDates.includes(weekdate.date.valueOf()) ? 'coolgray' : 'darkestblue'}--text`">
                                {{ weekdate.formatted }}
                              </div>
                            </v-col>

                            <v-spacer />

                            <v-col
                              cols="auto"
                              class="pa-0 mr-2"
                            >
                              <v-chip
                                :color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey lighten-1' : 'primary'"
                                :text-color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey darken-2' : 'white'"
                                small
                                class="font-weight-bold"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'clock']"
                                  class="mr-2 clock-icon-12px"
                                />
                                {{ durationFormatter(weekdate.total) }}
                              </v-chip>
                            </v-col>

                            <v-col
                              v-if="enableBulkOperations"
                              cols="auto"
                              class="pa-0"
                            >
                              <Checkbox
                                :input-value="areAllDayEntriesSelected(weekdate.entries)"
                                :indeterminate="areSomeDayEntriesSelected(weekdate.entries) && !areAllDayEntriesSelected(weekdate.entries)"
                                :disabled="lockedDates.includes(weekdate.date.valueOf()) || getFilteredEntries(weekdate.entries).length === 0"
                                class="mt-0 mr-1"
                                @change="toggleDaySelection(weekdate.entries, $event)"
                              />
                            </v-col>

                            <v-col
                              cols="auto"
                              class="pa-0"
                            >
                              <v-btn
                                icon
                                small
                                color="primary"
                                :disabled="lockedDates.includes(weekdate.date.valueOf())"
                                @click="openTimeEntryFormCreate(weekdate.dateValue)"
                              >
                                <font-awesome-icon :icon="['fas', 'plus']" />
                              </v-btn>
                            </v-col>
                          </v-row>
                        </v-card-title>

                        <!-- WeekDate's Entries Cards -->
                        <v-card-text v-if="getFilteredEntries(weekdate.entries).length">
                          <v-row class="time-entries-grid">
                            <v-col
                              v-for="item in getFilteredEntries(weekdate.entries)"
                              :key="item.timeEntryId"
                              cols="12"
                              sm="6"
                              md="4"
                              lg="3"
                            >
                              <v-card
                                class="time-entry-card"
                                :class="{ 'entry-selected': isEntrySelected(item.timeEntryId) }"
                                outlined
                              >
                                <!-- Card Header with Duration and Time Type -->
                                <v-card-title class="py-2 px-3 d-flex align-center">
                                  <!-- Duration -->
                                  <div class="d-flex align-center mr-3">
                                    <font-awesome-icon
                                      :icon="['fas', 'clock']"
                                      class="mr-2 coolgray--text clock-icon-12px"
                                    />

                                    <span class="body-2 font-weight-bold">
                                      {{ durationFormatter(item.duration) }}
                                    </span>
                                  </div>

                                  <v-spacer />

                                  <v-chip
                                    :color="getTimeTypeColor(item.timeClassificationName)"
                                    :text-color="getTimeTypeColor(item.timeClassificationName)"
                                    small
                                    outlined
                                    class="time-type-chip-header"
                                  >
                                    {{ item.timeClassificationName }}
                                  </v-chip>
                                </v-card-title>

                                <v-divider />

                                <!-- Card Body with Entry Details -->
                                <v-card-text class="py-2 px-3">
                                  <!-- Person Name (conditional) -->
                                  <div
                                    v-if="shouldShowPersonFilter"
                                    class="entry-info-row mb-2"
                                  >
                                    <font-awesome-icon
                                      :icon="['fas', 'user']"
                                      size="sm"
                                      class="icon-fixed-width primary--text"
                                    />
                                    <div class="entry-info-content">
                                      <span class="body-2 font-weight-bold">
                                        {{ getPersonDisplayName(item.personId) }}
                                      </span>
                                    </div>
                                  </div>

                                  <!-- Client & Billing Code - Icon-based -->
                                  <div class="entry-info-row mb-2">
                                    <font-awesome-icon
                                      :icon="['fas', 'building']"
                                      size="sm"
                                      class="icon-fixed-width primary--text"
                                    />
                                    <div class="entry-info-content">
                                      <v-tooltip top>
                                        <template v-slot:activator="{ on, attrs }">
                                          <span
                                            class="body-2 font-weight-medium"
                                            v-bind="attrs"
                                            v-on="on"
                                          >{{ item.billingCode }}</span>
                                        </template>
                                        <span>{{ item.clientName }}</span>
                                      </v-tooltip>
                                    </div>
                                  </div>

                                  <!-- Project - Icon-based (if exists) -->
                                  <div
                                    v-if="item.projectName"
                                    class="entry-info-row mb-2"
                                  >
                                    <font-awesome-icon
                                      :icon="['fas', 'folder']"
                                      size="sm"
                                      class="icon-fixed-width primary--text"
                                    />
                                    <div class="entry-info-content">
                                      <span class="body-2">{{ item.projectName }}</span>
                                    </div>
                                  </div>

                                  <!-- Notes Display (if exists) -->
                                  <div
                                    v-if="item.description"
                                    class="entry-notes-display mt-2"
                                  >
                                    <div class="body-2 work-notes-text">
                                      {{ item.description }}
                                    </div>
                                  </div>
                                </v-card-text>

                                <v-divider />

                                <!-- Card Actions -->
                                <v-card-actions class="py-2 px-3 justify-space-between">
                                  <!-- Action Icons Group -->
                                  <div
                                    class="d-flex align-center"
                                    role="group"
                                    aria-label="Time entry actions"
                                  >
                                    <v-tooltip top>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                          icon
                                          :disabled="lockedDates.includes(weekdate.dateValue)"
                                          class="mr-1 action-btn action-btn-duplicate"
                                          aria-label="Copy time entry"
                                          v-bind="attrs"
                                          v-on="on"
                                          @click.stop="openTimeEntryFormDuplicate(item, weekdate.dateValue)"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'copy']"
                                            aria-hidden="true"
                                          />
                                        </v-btn>
                                      </template>
                                      <span>Copy entry</span>
                                    </v-tooltip>

                                    <v-tooltip top>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                          icon
                                          :disabled="lockedDates.includes(weekdate.dateValue)"
                                          class="mr-1 action-btn action-btn-edit"
                                          aria-label="Edit time entry"
                                          v-bind="attrs"
                                          v-on="on"
                                          @click.stop="openTimeEntryFormUpdate(item)"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'pencil']"
                                            aria-hidden="true"
                                          />
                                        </v-btn>
                                      </template>
                                      <span>Edit entry</span>
                                    </v-tooltip>

                                    <v-tooltip top>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                          icon
                                          :disabled="lockedDates.includes(weekdate.dateValue)"
                                          class="action-btn action-btn-delete"
                                          aria-label="Delete time entry"
                                          v-bind="attrs"
                                          v-on="on"
                                          @click.stop="openTimeEntryFormDelete(item)"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'trash']"
                                            aria-hidden="true"
                                          />
                                        </v-btn>
                                      </template>
                                      <span>Delete entry</span>
                                    </v-tooltip>
                                  </div>

                                  <!-- Checkbox on Right -->
                                  <Checkbox
                                    v-if="enableBulkOperations"
                                    :input-value="isEntrySelected(item.timeEntryId)"
                                    :disabled="lockedDates.includes(weekdate.dateValue)"
                                    class="mt-0"
                                    aria-label="Select time entry"
                                    @change="toggleEntrySelection(item.timeEntryId, $event)"
                                  />
                                </v-card-actions>
                              </v-card>
                            </v-col>
                          </v-row>
                        </v-card-text>

                        <!-- Empty State Message -->
                        <v-card-text
                          v-else
                          class="text-center py-8 empty-state-clickable"
                          @click="openTimeEntryFormCreate(weekdate.dateValue)"
                        >
                          <v-row class="justify-center">
                            <v-col cols="12">
                              <font-awesome-icon
                                :icon="['fas', 'calendar-plus']"
                                size="3x"
                                class="coolgray--text mb-3"
                              />
                            </v-col>
                          </v-row>
                          <v-row class="justify-center">
                            <v-col cols="12">
                              <div class="text-h6 empty-state-text mb-2">
                                No time entries for {{ weekdate.formatted }}
                              </div>
                              <div class="body-2 empty-state-text mb-4">
                                Click here or the
                                <font-awesome-icon
                                  :icon="['fas', 'plus']"
                                  size="sm"
                                />
                                button above to add your first entry for this day.
                              </div>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-container>
                    </v-card>
                  </div>
                  <!-- End of calendar view mode -->

                  <!-- COLUMN CALENDAR VIEW - Week days in columns -->
                  <div v-else-if="weekViewMode === 'columns'">
                    <v-row class="time-entries-columns mt-0 mb-1">
                      <v-col
                        v-for="(weekdate, index) in currentWeek.week"
                        :key="index"
                        class="d-flex flex-column pt-0"
                      >
                        <!-- Day Column Header -->
                        <v-card
                          :class="`day-column-header ${setEntryClass(weekdate, index)}`"
                          outlined
                        >
                          <v-card-title class="py-2 px-2 d-flex flex-column align-center">
                            <!-- Day Name and Date -->
                            <div class="d-flex align-center justify-space-between full-width mb-2">
                              <div :class="`caption font-weight-bold text-uppercase day-column-date ${lockedDates.includes(weekdate.date.valueOf()) ? 'locked' : ''}`">
                                {{ weekdate.date.format('ddd, MMM D') }}
                              </div>

                              <v-btn
                                icon
                                x-small
                                color="primary"
                                :disabled="lockedDates.includes(weekdate.date.valueOf())"
                                @click="openTimeEntryFormCreate(weekdate.dateValue)"
                              >
                                <font-awesome-icon :icon="['fas', 'plus']" />
                              </v-btn>
                            </div>

                            <!-- Total Hours and Checkbox Row -->
                            <div class="d-flex align-center justify-space-between full-width">
                              <v-chip
                                :color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey lighten-1' : 'primary'"
                                :text-color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey darken-2' : 'white'"
                                small
                                class="font-weight-bold"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'clock']"
                                  class="mr-1 clock-icon-12px"
                                />
                                {{ durationFormatter(weekdate.total) }}
                              </v-chip>

                              <Checkbox
                                v-if="enableBulkOperations"
                                :input-value="areAllDayEntriesSelected(weekdate.entries)"
                                :indeterminate="areSomeDayEntriesSelected(weekdate.entries) && !areAllDayEntriesSelected(weekdate.entries)"
                                :disabled="lockedDates.includes(weekdate.date.valueOf()) || getFilteredEntries(weekdate.entries).length === 0"
                                class="mt-0"
                                @change="toggleDaySelection(weekdate.entries, $event)"
                              />
                            </div>
                          </v-card-title>
                        </v-card>

                        <v-divider />

                        <!-- Day Column Entries -->
                        <div class="day-column-entries flex-grow-1">
                          <v-card
                            v-for="item in getFilteredEntries(weekdate.entries)"
                            :key="item.timeEntryId"
                            class="time-entry-card-compact mb-2 mt-2"
                            :class="{ 'entry-selected': isEntrySelected(item.timeEntryId) }"
                            outlined
                          >
                            <!-- Compact Card Header -->
                            <v-card-title class="py-1 px-2 d-flex align-center card-header-compact">
                              <!-- Duration - Left side -->
                              <div class="d-flex align-center mr-2">
                                <font-awesome-icon
                                  :icon="['fas', 'clock']"
                                  class="mr-1 primary--text clock-icon-12px"
                                />

                                <span class="body-2 font-weight-bold">
                                  {{ durationFormatter(item.duration) }}
                                </span>
                              </div>

                              <v-spacer />

                              <v-chip
                                :color="getTimeTypeColor(item.timeClassificationName)"
                                :text-color="getTimeTypeColor(item.timeClassificationName)"
                                x-small
                                outlined
                                class="time-type-chip-compact"
                              >
                                {{ item.timeClassificationName }}
                              </v-chip>
                            </v-card-title>

                            <v-divider />

                            <!-- Compact Card Body -->
                            <v-card-text class="py-2 px-2">
                              <!-- Person Name (conditional, compact) -->
                              <div
                                v-if="shouldShowPersonFilter"
                                class="entry-info-row mb-1 align-center"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'user']"
                                  size="xs"
                                  class="icon-fixed-width coolgray--text"
                                />
                                <div class="entry-info-content">
                                  <span class="caption font-weight-bold">
                                    {{ getPersonDisplayName(item.personId) }}
                                  </span>
                                </div>
                              </div>

                              <!-- Client & Billing Code - Icon-based -->
                              <div class="entry-info-row mb-1 align-center">
                                <font-awesome-icon
                                  :icon="['fas', 'building']"
                                  size="xs"
                                  class="icon-fixed-width coolgray--text"
                                />
                                <div class="entry-info-content">
                                  <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                      <span
                                        class="caption font-weight-medium"
                                        v-bind="attrs"
                                        v-on="on"
                                      >{{ item.billingCode }}</span>
                                    </template>
                                    <span>{{ item.clientName }}</span>
                                  </v-tooltip>
                                </div>
                              </div>

                              <!-- Project - Icon-based (if exists) -->
                              <div
                                v-if="item.projectName"
                                class="entry-info-row mb-1 align-center"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'folder']"
                                  size="xs"
                                  class="icon-fixed-width coolgray--text"
                                />
                                <div class="entry-info-content">
                                  <span class="caption">{{ item.projectName }}</span>
                                </div>
                              </div>

                              <!-- Notes - No Label (if exists) -->
                              <div
                                v-if="item.description"
                                class="entry-notes-display mt-2"
                              >
                                <div class="caption work-notes-text">
                                  {{ item.description }}
                                </div>
                              </div>
                            </v-card-text>

                            <v-divider />

                            <!-- Compact Card Actions -->
                            <v-card-actions class="py-2 px-2 justify-space-between">
                              <!-- Action Icons Group -->
                              <div
                                class="d-flex align-center"
                                role="group"
                                aria-label="Time entry actions"
                              >
                                <v-tooltip top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      small
                                      :disabled="lockedDates.includes(weekdate.dateValue)"
                                      class="mr-1 action-btn action-btn-duplicate"
                                      aria-label="Copy time entry"
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="openTimeEntryFormDuplicate(item, weekdate.dateValue)"
                                    >
                                      <font-awesome-icon
                                        :icon="['fas', 'copy']"
                                        aria-hidden="true"
                                      />
                                    </v-btn>
                                  </template>
                                  <span>Copy</span>
                                </v-tooltip>

                                <v-tooltip top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      small
                                      :disabled="lockedDates.includes(weekdate.dateValue)"
                                      class="mr-1 action-btn action-btn-edit"
                                      aria-label="Edit time entry"
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="openTimeEntryFormUpdate(item)"
                                    >
                                      <font-awesome-icon
                                        :icon="['fas', 'pencil']"
                                        aria-hidden="true"
                                      />
                                    </v-btn>
                                  </template>
                                  <span>Edit</span>
                                </v-tooltip>

                                <v-tooltip top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      icon
                                      small
                                      :disabled="lockedDates.includes(weekdate.dateValue)"
                                      class="action-btn action-btn-delete"
                                      aria-label="Delete time entry"
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="openTimeEntryFormDelete(item)"
                                    >
                                      <font-awesome-icon
                                        :icon="['fas', 'trash']"
                                        aria-hidden="true"
                                      />
                                    </v-btn>
                                  </template>
                                  <span>Delete</span>
                                </v-tooltip>
                              </div>

                              <!-- Checkbox on Right -->
                              <Checkbox
                                v-if="enableBulkOperations"
                                :input-value="isEntrySelected(item.timeEntryId)"
                                :disabled="lockedDates.includes(weekdate.dateValue)"
                                class="mt-0"
                                aria-label="Select time entry"
                                @change="toggleEntrySelection(item.timeEntryId, $event)"
                              />
                            </v-card-actions>
                          </v-card>

                          <!-- Empty State for Day Column -->
                          <v-card
                            v-if="getFilteredEntries(weekdate.entries).length === 0"
                            class="mt-2 empty-state-clickable"
                            outlined
                            hover
                            @click="openTimeEntryFormCreate(weekdate.dateValue)"
                          >
                            <v-card-text class="text-center py-4">
                              <font-awesome-icon
                                :icon="['fas', 'calendar-plus']"
                                size="2x"
                                class="coolgray--text mb-2"
                              />
                              <div class="caption coolgray--text">
                                No entries<br>
                                <span class="caption font-weight-light">Click to add</span>
                              </div>
                            </v-card-text>
                          </v-card>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                  <!-- End of column calendar view -->
                </v-col>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- List Views (MTD, Billable, Projects) -->
        <v-row
          v-else
          class="body-2 mt-0"
        >
          <v-col>
            <!-- Card View -->
            <v-row v-if="listViewMode === 'cards'">
              <v-col
                v-for="entry in filteredTimes"
                :key="entry.timeEntryId"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card
                  class="time-entry-card-list"
                  outlined
                >
                  <!-- Card Header with Duration and Time Type -->
                  <v-card-title class="py-2 px-3 d-flex align-center">
                    <!-- Duration -->
                    <div class="d-flex align-center mr-3">
                      <font-awesome-icon
                        :icon="['fas', 'clock']"
                        class="mr-2 coolgray--text clock-icon-12px"
                      />
                      <span class="body-2 font-weight-bold">
                        {{ durationFormatter(entry.duration) }}
                      </span>
                    </div>

                    <v-spacer />

                    <!-- Date Badge -->
                    <v-chip
                      x-small
                      outlined
                      color="primary"
                      class="mr-2"
                    >
                      {{ formatDate(entry.date) }}
                    </v-chip>

                    <!-- Time Type Chip -->
                    <v-chip
                      :color="getTimeClassificationColor(entry.timeClassificationName)"
                      :text-color="getTimeClassificationColor(entry.timeClassificationName)"
                      small
                      outlined
                      class="time-type-chip-header"
                    >
                      {{ entry.timeClassificationName }}
                    </v-chip>
                  </v-card-title>

                  <v-divider />

                  <!-- Card Body with Entry Details -->
                  <v-card-text class="py-2 px-3">
                    <!-- Person Name (conditional) -->
                    <div
                      v-if="shouldShowPersonFilter"
                      class="entry-info-row mb-2"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'user']"
                        size="sm"
                        class="icon-fixed-width primary--text"
                      />
                      <div class="entry-info-content">
                        <span class="body-2 font-weight-bold">
                          {{ entry.personName || 'N/A' }}
                        </span>
                      </div>
                    </div>

                    <!-- Client & Billing Code - Icon-based -->
                    <div class="entry-info-row mb-2">
                      <font-awesome-icon
                        :icon="['fas', 'building']"
                        size="sm"
                        class="icon-fixed-width primary--text"
                      />
                      <div class="entry-info-content">
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <span
                              class="body-2 font-weight-medium"
                              v-bind="attrs"
                              v-on="on"
                            >{{ entry.billingCode || 'No billing code' }}</span>
                          </template>
                          <span>{{ entry.clientName || 'N/A' }}</span>
                        </v-tooltip>
                      </div>
                    </div>

                    <!-- Project - Icon-based (if exists) -->
                    <div
                      v-if="entry.projectName"
                      class="entry-info-row mb-2"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'folder']"
                        size="sm"
                        class="icon-fixed-width primary--text"
                      />
                      <div class="entry-info-content">
                        <span class="body-2">{{ entry.projectName }}</span>
                      </div>
                    </div>

                    <!-- Notes Display (if exists) -->
                    <div
                      v-if="entry.description || entry.workNotes"
                      class="entry-notes-display mt-2"
                    >
                      <div class="body-2 work-notes-text">
                        {{ entry.description || entry.workNotes }}
                      </div>
                    </div>
                  </v-card-text>

                  <v-divider />

                  <!-- Card Actions -->
                  <v-card-actions class="px-2 py-1 justify-end">
                    <v-btn
                      icon
                      color="primary"
                      @click.stop="openTimeEntryFormDuplicate(entry, entry.date)"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <font-awesome-icon
                            style="outline-color: transparent;"
                            :icon="['fas', 'copy']"
                            size="lg"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span>Copy</span>
                      </v-tooltip>
                    </v-btn>

                    <v-btn
                      icon
                      color="primary"
                      @click.stop="openTimeEntryFormUpdate(entry)"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <font-awesome-icon
                            style="outline-color: transparent;"
                            :icon="['fas', 'edit']"
                            size="lg"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span>Edit</span>
                      </v-tooltip>
                    </v-btn>

                    <v-btn
                      icon
                      color="error"
                      @click.stop="openTimeEntryFormDelete(entry)"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <font-awesome-icon
                            style="outline-color: transparent;"
                            :icon="['fas', 'trash']"
                            size="lg"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span>Delete</span>
                      </v-tooltip>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>

              <!-- Empty State -->
              <v-col
                v-if="filteredTimes.length === 0"
                cols="12"
              >
                <v-card
                  outlined
                  class="text-center pa-8"
                >
                  <font-awesome-icon
                    :icon="['fas', 'inbox']"
                    size="3x"
                    class="coolgray--text mb-3"
                  />
                  <div class="text-h6 coolgray--text">
                    No time entries found
                  </div>
                  <div class="body-2 coolgray--text mt-2">
                    Try adjusting your filters or date range
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Table View -->
            <data-table
              v-else
              ref="table"
              :actions="actions"
              :data-groups="currentDataGroups"
              :initial-items-per-page="10"
              :hide-toolbar-buttons="true"
            />
          </v-col>
        </v-row>

        <time-modal
          :time-shift="timeShift"
          :clients="clients"
          :persons="persons"
          :projects="projectsResponse"
          :services="servicesResponse"
          :time-types="timeTypes"
          :time-entries="times"
          :week="week"
        />

        <!-- Bulk Operation Confirmation Dialog -->
        <v-dialog
          v-if="enableBulkOperations"
          v-model="bulkOperationDialog"
          max-width="500"
        >
          <v-card>
            <v-card-title class="headline">
              {{ bulkOperationType === 'delete' ? 'Delete Multiple Entries' : 'Copy Entries' }}
            </v-card-title>

            <v-card-text>
              <div v-if="bulkOperationType === 'delete'">
                <p class="mb-2">
                  Are you sure you want to delete {{ selectedEntries.length }} time {{ selectedEntries.length === 1 ? 'entry' : 'entries' }}?
                </p>
                <v-alert
                  type="warning"
                  text
                  dense
                >
                  This action cannot be undone.
                </v-alert>
              </div>

              <div v-else-if="bulkOperationType === 'copy'">
                <p class="mb-4">
                  Copy {{ selectedEntries.length }} time {{ selectedEntries.length === 1 ? 'entry' : 'entries' }} to a new date:
                </p>

                <v-menu
                  ref="copyDateMenu"
                  v-model="copyToDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="copyToDate"
                      label="Copy To Date"
                      readonly
                      outlined
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <font-awesome-icon
                          :icon="['fas', 'calendar']"
                          class="mr-2"
                        />
                      </template>
                    </v-text-field>
                  </template>

                  <v-date-picker
                    v-model="copyToDate"
                    no-title
                    scrollable
                    @input="copyToDateMenu = false"
                  />
                </v-menu>
              </div>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                @click="bulkOperationDialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :color="bulkOperationType === 'delete' ? 'error' : 'primary'"
                @click="confirmBulkOperation"
              >
                {{ bulkOperationType === 'delete' ? 'Delete' : 'Copy' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Keyboard Shortcuts Help Dialog -->
        <v-dialog
          v-model="showKeyboardShortcuts"
          max-width="500"
        >
          <v-card>
            <v-card-title class="headline primary white--text">
              <font-awesome-icon
                :icon="['fas', 'keyboard']"
                class="mr-2"
              />
              Keyboard Shortcuts
            </v-card-title>

            <v-card-text class="pt-4">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        ←
                      </v-chip>
                      Previous Week
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        →
                      </v-chip>
                      Next Week
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        T
                      </v-chip>
                      Go to Today / Current Week
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        N
                      </v-chip>
                      New Entry (Today)
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                      >
                        ESC
                      </v-chip>
                      Clear Selection
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <v-divider class="my-3" />

              <p class="caption coolgray--text mb-0">
                Shortcuts work in the Time Entry tab when not typing in an input field.
              </p>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                text
                @click="showKeyboardShortcuts = false"
              >
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <!-- Floating Action Button for Add Entry (mobile/tablet only, all tabs) -->
    <v-btn
      v-if="!enableBulkOperations || selectedEntries.length === 0"
      fab
      fixed
      bottom
      right
      color="primary"
      class="mb-4 mr-4 d-md-none"
      @click="openTimeEntryFormCreate(today)"
    >
      <font-awesome-icon
        :icon="['fas', 'plus']"
        size="lg"
      />
    </v-btn>
  </v-col>
</template>

<script>
/**
 * Time Component - Tab System Documentation
 *
 * This component uses a flexible tab system that supports different date ranges
 * and filtering for each tab view.
 *
 * 📚 For detailed documentation on adding new tabs, see:
 *    - Full Guide: docs/time-component-tabs.md
 *    - Quick Reference: docs/time-tabs-quick-reference.md
 *
 * Key Features:
 * - Smart data fetching (only refetches when date ranges differ)
 * - Client-side filtering for instant tab switching
 * - Flexible date range configuration per tab
 * - URL-based tab and week state management (bookmarkable URLs)
 * - Automatic week navigation data fetching
 * - URL cleanup: timeShift parameters removed when switching away from Time Entry tab
 * - Automatic removal of unrecognized query parameters to maintain clean URLs
 * - Local summary calculations that update reactively when tabs or date ranges change
 */

import { createNamespacedHelpers } from 'vuex'

import dayjs from 'dayjs'
import dates from 'src/utils/dates'
import format from 'src/utils/format'
import Checkbox from '@/tools/components/Checkbox.vue'
import ColumnPicker from '@/tools/components/table/ColumnPicker.vue'
import durations from '../store/utils/durations'

import mixin from '../components/mixin'
import api from '@/store/api.js'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')
const {
  mapGetters: mapToolsGetters,
  mapState: mapToolsState,
} = createNamespacedHelpers('tools')

export default {
  name: 'TimePage',
  components: {
    Checkbox,
    ColumnPicker,
    DateRangePickerButton: () => import('@/tools/components/DateRangePickerButton'),
    TimeModal: () => import('src/internal/components/TimeModal'),
  },
  mixins: [mixin],
  data() {
    return {
      actions: [
        {
          event: 'internal/openTimeEntryFormUpdate',
          name: 'Edit',
        },
        {
          event: 'internal/openTimeEntryFormDelete',
          name: 'Delete',
        },
      ],
      lockedClass: '', // 'offwhite'
      applyingDateFilter: false,
      loading: false,
      error: null,
      times: [], // Component-level time entries data
      tableSearch: '', // Search filter for the table
      // Quick filters
      selectedPersonFilter: [], // Array of person IDs - initialized in mounted()
      selectedTimeTypeFilter: null,
      selectedClientFilter: null,
      selectedProjectFilter: null,
      filterMenuOpen: false,
      // Separate date ranges for each view type
      dateRanges: {
        'time-entry': (() => {
          // Current week range for time entry view (weekly calendar)
          const week = dates.getWeek(0) // Current week
          const startOfWeek = week[0] // Monday
          const endOfWeek = week[6] // Sunday
          return [dates.getFormattedDate(startOfWeek), dates.getFormattedDate(endOfWeek)]
        })(),
        mtd: dates.mtdFormatted(), // MTD for month-to-date view (data table filtering)
        billable: dates.mtdFormatted(), // MTD for billable view (data table filtering)
        projects: dates.mtdFormatted(), // MTD for projects view (data table filtering)
        // Example future tab:
        // 'last-month': dates.lastMonthFormatted(), // Last month range
      },
      activeTab: null, // Will be set based on showTimeEntryList computed property
      activeTabMap: {
        0: 'time-entry',
        1: 'mtd',
        2: 'billable',
        3: 'projects',
      },

      tabs: [
        {
          id: 'time-entry',
          label: 'Time Entry',
          icon: ['fas', 'calendar-week'],
        },
        {
          id: 'mtd',
          label: 'Month-To-Date',
          icon: ['fas', 'calendar-days'],
        },
        {
          id: 'billable',
          label: 'Billable',
          icon: ['fas', 'dollar-sign'],
        },
        {
          id: 'projects',
          label: 'Projects',
          icon: ['fas', 'folder-open'],
        },
      ],

      customTimeShiftText: [null, -1, 0, 1], // Values that do not need "from Current Week" text

      // Feature flags
      enableBulkOperations: false, // DISABLED: Bulk selection feature not ready

      // Bulk operations
      selectedEntries: [], // Array of selected entry IDs
      bulkOperationDialog: false,
      bulkOperationType: null, // 'delete' or 'copy'
      copyToDate: null,
      copyToDateMenu: false,

      // Entry templates
      entryTemplates: [],
      templateDialog: false,
      templateName: '',
      selectedTemplate: null,

      // Keyboard shortcuts help
      showKeyboardShortcuts: false,

      // Column picker dialog
      columnPickerDialog: false,

      // View mode toggle: 'calendar' (list view) or 'columns' (week columns)
      weekViewMode: 'columns', // Default to column calendar view

      // List view mode toggle for MTD/Billable/Projects: 'cards' or 'table'
      listViewMode: 'cards', // Default to card view for better mobile UX
    }
  },
  computed: {
    ...mapAuthGetters(['inDarkMode']),
    ...mapLayoutState({
      navCollapsed: (state) => state.navCollapsed,
    }),
    ...mapToolsGetters([
      'storedItems', // Still need for organizations/clients
      'timeTypes', // Still need for time entry forms
      'showSummaries', // Still need for summary card display
      'persons', // Still need for time entry forms
      'reportKey', // For column picker
      'tableColumns', // For column picker
    ]),

    ...mapToolsState({
      projectsResponse: (state) => state.projectsResponse,
      servicesResponse: (state) => state.servicesResponse,
      requiredColumns: (state) => state.requiredColumns,
    }),

    // Component data is already filtered to current user, so times = ownTimes
    ownTimes() {
      return this.times || []
    },

    showTimeEntryList: {
      get() {
        // This logic should allow recovery from an invalid query string value.
        return this.$route.query.report === 'time-entry'
          || this.$route.query.report === 'mtd'
          || this.$route.query.report === 'billable'
          || this.$route.query.report === 'projects'
          ? this.$route.query.report
          : 'time-entry'
      },
      set(value) {
        const validValues = ['time-entry', 'mtd', 'billable', 'projects']

        if (!validValues.includes(value)) {
          return false
        }

        // Get the current value to avoid unnecessary route updates.
        const currentQuery = this.$route.query || {}
        const cleanedQuery = this.cleanQueryParameters(currentQuery)

        // Update route's query string.
        cleanedQuery.report = value
        this.$router.push({ query: cleanedQuery })

        return true
      },
    },
    tabIndex() {
      // Convert report string to tab index for v-tabs
      const tabMap = {
        'time-entry': 0,
        mtd: 1,
        billable: 2,
        projects: 3,
      }
      return tabMap[this.showTimeEntryList] || 0
    },

    /**
     * Tabs with computed counts for each tab
     * Shows total count per tab (filters only affect active tab's data table)
     */
    tabsWithCounts() {
      const allTimes = this.times || []

      return this.tabs.map((tab) => {
        let count = 0

        // Get count based on tab type (no filters applied to maintain static tab counts)
        switch (tab.id) {
          case 'time-entry': {
            // For time-entry, count entries in the current week/date range
            const weekEntries = this.filteredTimesForTab('time-entry')
            count = weekEntries.length
            break
          }

          case 'mtd': {
            // For MTD, count all entries in the date range
            const mtdEntries = this.filteredTimesForTab('mtd')
            count = mtdEntries.length
            break
          }

          case 'billable': {
            // For billable, count only billable entries
            count = allTimes.filter((entry) => entry.timeClassificationName === 'BILLABLE').length
            break
          }

          case 'projects': {
            // For projects, count entries with projects assigned
            count = allTimes.filter((entry) => (entry?.projectId && entry.projectId !== -1) || entry.timeClassificationName === 'PROJECT').length
            break
          }

          default:
            count = 0
        }

        return { ...tab, count }
      })
    },

    // Current required columns for the column picker
    currentRequiredColumns() {
      return this.requiredColumns[this.reportKey] || []
    },

    currentDateRange() {
      // For time-entry view, check URL parameters first, then custom date range, then timeShift
      if (this.showTimeEntryList === 'time-entry') {
        // Check if we have custom date range in URL parameters
        const { startDate, endDate } = this.$route.query
        if (startDate && endDate) {
          return [startDate, endDate]
        }

        // If there's a stored custom date range for time-entry, use it
        if (this.dateRanges['time-entry'] && this.hasCustomTimeEntryRange) {
          return this.dateRanges['time-entry']
        }

        // Otherwise, use the current timeShift to calculate the week range
        const timeShiftValue = this.timeShift !== null ? this.timeShift : 0
        const week = dates.getWeek(timeShiftValue)
        const startOfWeek = week[0] // Monday
        const endOfWeek = week[6] // Sunday
        return [dates.getFormattedDate(startOfWeek), dates.getFormattedDate(endOfWeek)]
      }

      // For other views, return the appropriate date range
      return this.dateRanges[this.showTimeEntryList] || this.dateRanges.mtd
    },

    hasCustomTimeEntryRange() {
      // Get custom date range from URL parameters.
      const { startDate, endDate } = this.$route.query

      // If both startDate and endDate are present in URL, return true.
      if (startDate && endDate) {
        return true
      }

      // If no stored range, return false.
      if (!this.dateRanges['time-entry']) {
        return false
      }

      // Get the stored date range for the time-entry view.
      const storedRange = this.dateRanges['time-entry']

      // Get timeShift. Default is current week's timeShift (0).
      const timeShiftValue = this.timeShift !== null ? this.timeShift : 0

      // Calculate the timeShift week's date range (Monday to Sunday).
      const timeShiftWeek = dates.getWeek(timeShiftValue)

      // Format dates to 'YYYY-MM-DD' for comparison.
      const timeShiftWeekStart = dates.getFormattedDate(timeShiftWeek[0])
      const timeShiftWeekEnd = dates.getFormattedDate(timeShiftWeek[6])

      // Compare stored range with timeShift's week range.
      // If startDate differs from timeShiftWeekStart, return true.
      // If endDate differs from timeShiftWeekEnd, return true.
      return !(storedRange[0] === timeShiftWeekStart && storedRange[1] === timeShiftWeekEnd)
    },

    today() {
      return dates.now()
    },
    timeShift: {
      get() {
        // Check if we have custom date range parameters first
        const { startDate, endDate } = this.$route.query
        if (startDate && endDate) {
          // Return null to indicate this is a custom range, not a standard week shift
          return null
        }

        // Get timeShift from URL query string first, fallback to store
        const queryShift = this.$route.query.week
        if (queryShift !== undefined) {
          const parsedShift = parseInt(queryShift, 10)
          // Validate the parsed value is a valid number
          if (!Number.isNaN(parsedShift)) {
            return parsedShift
          }
        }
        // Fallback to store value
        return this.$store.state.internal?.timeShift || 0
      },
      set(value) {
        // Ensure the value is a valid integer
        const timeShiftValue = Math.floor(value)

        // Update the store (check if module is registered)
        if (this.$store.hasModule && this.$store.hasModule('internal')) {
          this.$store.commit('internal/timeShift', timeShiftValue)
        }

        // Clean and update the URL query string
        const cleanedQuery = this.cleanQueryParameters(this.$route.query)

        // Clear any custom date range parameters when using timeShift
        delete cleanedQuery.startDate
        delete cleanedQuery.endDate

        if (timeShiftValue === 0) {
          // Remove week parameter for current week (default)
          delete cleanedQuery.week
        } else {
          cleanedQuery.week = timeShiftValue.toString()
        }

        // Only update route if query actually changed
        const currentWeek = this.$route.query.week
        const newWeek = cleanedQuery.week
        const hadCustomRange = this.$route.query.startDate || this.$route.query.endDate

        if (currentWeek !== newWeek || hadCustomRange) {
          this.$router.push({ query: cleanedQuery })
        }

        return true
      },
    },
    clients() {
      const allOrgs = this.storedItems?.organizations?.all || []
      return allOrgs.filter((org) => org.isTimesheetEnabled)
    },
    timeEntryHeaders() {
      return [
        {
          text: '',
          value: 'select',
          class: 'select-column',
          sortable: false,
          width: '50px',
        },
        {
          text: 'Billing Code',
          value: 'billingCode',
          class: 'org-code font-weight-medium',
          sortable: true,
        },
        {
          text: 'Organization',
          value: 'clientName',
          class: 'org-name font-weight-medium',
          sortable: true,
        },
        {
          text: 'Project',
          value: 'projectName',
          class: 'project-name font-weight-medium',
          sortable: true,
        },
        {
          text: 'Time Type',
          value: 'timeClassificationName',
          class: 'time-type font-weight-medium',
          sortable: true,
        },
        {
          text: 'HH:MM',
          value: 'duration',
          class: 'entry-duration font-weight-medium',
          sortable: true,
        },
        {
          text: 'Notes',
          value: 'description',
          class: 'work-notes font-weight-medium',
          sortable: true,
        },
        {
          text: 'Actions',
          value: 'actions',
          class: 'entry-actions font-weight-medium',
          sortable: false,
        },
      ]
    },
    // Quick filter options
    personFilterOptions() {
      const allEntries = this.getAllEntriesFromTimeView()
      const allPersons = this.persons || []

      // Create a lookup map for persons by personId
      const personsById = allPersons.reduce((map, person) => {
        if (person.personId) {
          map[person.personId] = person
        }
        return map
      }, {})

      // Get unique person IDs from entries
      const uniquePersonIds = [...new Set(allEntries.map((entry) => entry.personId).filter(Boolean))]

      // Map to person display names or emails
      return uniquePersonIds
        .map((personId) => {
          const person = personsById[personId]
          if (person) {
            const displayText = person.displayName || person.email || `${person.firstName} ${person.lastName}` || `Person ${personId}`
            return { text: displayText, value: personId }
          }
          return null
        })
        .filter(Boolean)
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    timeTypeFilterOptions() {
      const allEntries = this.getAllEntriesFromTimeView()
      const uniqueTypes = [...new Set(allEntries.map((entry) => entry.timeClassificationName).filter(Boolean))]
      return uniqueTypes.sort().map((type) => ({ text: type, value: type }))
    },
    clientFilterOptions() {
      const allEntries = this.getAllEntriesFromTimeView()
      const uniqueClients = [...new Set(allEntries.map((entry) => entry.clientName).filter(Boolean))]
      return uniqueClients.sort().map((client) => ({ text: client, value: client }))
    },
    projectFilterOptions() {
      const allEntries = this.getAllEntriesFromTimeView()
      const uniqueProjects = [...new Set(allEntries.map((entry) => entry.projectName).filter(Boolean))]
      return uniqueProjects.sort().map((project) => ({ text: project, value: project }))
    },
    shouldShowPersonFilter() {
      // Only show person filter if there are multiple persons with accessible time entries
      return this.personFilterOptions.length > 1
    },
    hasActiveFilters() {
      return !!(this.selectedPersonFilter.length > 0 || this.selectedTimeTypeFilter || this.selectedClientFilter || this.selectedProjectFilter)
    },
    activeFilterCount() {
      let count = 0
      if (this.selectedPersonFilter.length > 0) count += 1
      if (this.selectedTimeTypeFilter) count += 1
      if (this.selectedClientFilter) count += 1
      if (this.selectedProjectFilter) count += 1
      return count
    },
    week() {
      return dates.getWeek(this.timeShift)
    },
    currentWeekNumber() {
      const { week } = this
      if (!week || !week[0]) return ''
      return week[0].week()
    },
    currentYear() {
      const { week } = this
      if (!week || !week[0]) return ''
      return week[0].year()
    },
    timeShiftText() {
      const { timeShift } = this

      // Handle custom date range case
      if (timeShift === null) {
        return 'Custom Range'
      }

      if (timeShift === 0) {
        return 'Current Week'
      }
      if (timeShift === 1) {
        return 'Next Week'
      }
      if (timeShift === -1) {
        return 'Last Week'
      }
      if (timeShift > 1) {
        return `+${timeShift}`
      }
      return `${timeShift}` // Already has negative sign for negative values
    },
    getWeekDateRange() {
      const { week } = this
      const [startDate,,,,,, endDate] = week
      const { $M: startMonth } = startDate || {}
      const { $M: endMonth } = endDate || {}
      const sameMonth = startMonth === endMonth

      return sameMonth
        ? this.sameMonthText(startDate, endDate)
        : this.crossMonthText(startDate, endDate)
    },
    lockedDates() {
      const { week } = this
      // Get getters dynamically because store is imported dynamically.
      const maxDate = this.$store.getters['internal/maxCalendarDate']
      const minDate = this.$store.getters['internal/minCalendarDate']
      const seed = []

      return week.reduce((locked, weekdate) => {
        const valid = dates.areDatesInRange(weekdate, minDate, maxDate)

        if (!valid) locked.push(weekdate.valueOf())

        return locked
      }, seed)
    },
    currentWeek() {
      const { ownTimes, week } = this
      // Prevent pollution during nav.
      const seedWeek = week.map((date) => ({ date, entries: [], formatted: date.format('dddd, MMMM DD'), total: 0 }))
      const currentWeek = { week: seedWeek, total: 0 }

      if (!ownTimes.length) return currentWeek

      currentWeek.week = seedWeek.map((day) => {
        const results = this.getEntriesForDay(day.date)
        const totalMinutes = this.getTotal(results)

        currentWeek.total += totalMinutes

        day.dateValue = day.date.valueOf() // Example: 1759291200000
        day.entries = results
        day.total += totalMinutes

        return day
      })

      return currentWeek
    },

    // Projects lookup helper for filtering
    projectsById() {
      const projects = this.projectsResponse || []
      return projects.reduce((lookup, project) => {
        if (project.projectId) {
          lookup[project.projectId] = project
        }
        return lookup
      }, {})
    },

    // Get filtered times based on the active tab
    // 📝 To add filtering for new tabs, see docs/time-tabs-quick-reference.md
    filteredTimes() {
      if (!Array.isArray(this.times)) {
        return []
      }

      let filtered = []
      switch (this.showTimeEntryList) {
        case 'billable':
          // Filter for billable entries only
          filtered = this.times.filter((entry) => entry.timeClassificationName === 'BILLABLE')
          break

        case 'projects':
          // Filter for entries that have a project assigned (projectId exists and is not -1)
          filtered = this.times.filter((entry) => (entry?.projectId && entry.projectId !== -1) || entry.timeClassificationName === 'PROJECT')
          break

        default:
          filtered = this.times
      }

      // Apply search and filter controls (person, time type, client, project, search text)
      return this.getFilteredEntries(filtered)
    },

    // Data groups for the DataTable based on the active tab
    currentDataGroups() {
      const filteredItems = this.filteredTimes

      return [{
        groupName: null,
        isPartOfGroup: false,
        items: filteredItems,
        totalCount: filteredItems.length,
      }]
    },

    // Local summary calculations based on filtered time entries
    localTimeSummaries() {
      const entries = this.filteredTimes || []

      // Initialize counters
      let billableTotal = 0
      let nonBillableTotal = 0
      let projectTotal = 0
      let adxTotal = 0
      let adxPgTotal = 0
      let grandTotal = 0

      // Calculate totals based on filtered entries
      entries.forEach((entry) => {
        const duration = entry.duration || 0
        grandTotal += duration

        // Categorize by time classification
        const timeClass = entry.timeClassificationName || ''

        if (timeClass === 'BILLABLE') {
          billableTotal += duration
        } else if (timeClass === 'NON-BILLABLE') {
          nonBillableTotal += duration
        } else if (timeClass === 'PROJECT') {
          projectTotal += duration
        } else if (timeClass === 'ADX') {
          adxTotal += duration
        } else if (timeClass === 'ADX-PG') {
          adxPgTotal += duration
        }
      })

      // Return summary objects matching the expected format
      return {
        BILLABLE: {
          id: 'BILLABLE',
          title: 'Billable',
          subtitle: this.getSummarySubtitle(),
          total: billableTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        'NON-BILLABLE': {
          id: 'NON-BILLABLE',
          title: 'Non-Billable',
          subtitle: this.getSummarySubtitle(),
          total: nonBillableTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        PROJECT: {
          id: 'PROJECT',
          title: 'Project',
          subtitle: this.getSummarySubtitle(),
          total: projectTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        ADX: {
          id: 'ADX',
          title: 'AdX',
          subtitle: this.getSummarySubtitle(),
          total: adxTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        'ADX-PG': {
          id: 'ADX-PG',
          title: 'AdX-PG',
          subtitle: this.getSummarySubtitle(),
          total: adxPgTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        TOTAL: {
          id: 'TOTAL',
          title: 'Total',
          subtitle: this.getSummarySubtitle(),
          total: grandTotal,
          format: 'hhmm',
          styles: {
            background: 'white',
            text: 'darkestblue',
            value: 'darkestblue',
          },
        },
      }
    },

    // Progress segments for stacked bar visualization
    progressSegments() {
      const summaries = this.localTimeSummaries
      const total = summaries.TOTAL.total || 1 // Avoid division by zero

      // Define segments with colors - only include non-zero values
      const segments = [
        {
          id: 'BILLABLE',
          label: 'Billable',
          value: summaries.BILLABLE.total,
          colorClass: 'segment-billable',
          percentage: (summaries.BILLABLE.total / total) * 100,
        },
        {
          id: 'PROJECT',
          label: 'Project',
          value: summaries.PROJECT.total,
          colorClass: 'segment-project',
          percentage: (summaries.PROJECT.total / total) * 100,
        },
        {
          id: 'NON-BILLABLE',
          label: 'Non-Billable',
          value: summaries['NON-BILLABLE'].total,
          colorClass: 'segment-nonbillable',
          percentage: (summaries['NON-BILLABLE'].total / total) * 100,
        },
        {
          id: 'ADX',
          label: 'AdX',
          value: summaries.ADX.total,
          colorClass: 'segment-adx',
          percentage: (summaries.ADX.total / total) * 100,
        },
        {
          id: 'ADX-PG',
          label: 'AdX-PG',
          value: summaries['ADX-PG'].total,
          colorClass: 'segment-adxpg',
          percentage: (summaries['ADX-PG'].total / total) * 100,
        },
      ]

      // Filter out segments with zero values
      return segments.filter((segment) => segment.value > 0)
    },
  },
  watch: {
    showTimeEntryList: {
      async handler(newValue, oldValue) {
        // Keep activeTab in sync with showTimeEntryList
        this.activeTab = this.tabIndex

        // Clean up URL parameters when switching tabs
        const currentQuery = this.$route.query
        const cleanedQuery = this.cleanQueryParameters(currentQuery)

        // When switching away from time-entry view, remove timeShift-related parameters
        if (oldValue === 'time-entry' && newValue !== 'time-entry') {
          delete cleanedQuery.week
          delete cleanedQuery.startDate
          delete cleanedQuery.endDate
        }

        // Check if the cleaned query is different from current query
        const currentQueryString = JSON.stringify(currentQuery)
        const cleanedQueryString = JSON.stringify(cleanedQuery)

        if (currentQueryString !== cleanedQueryString) {
          this.$router.push({ query: cleanedQuery })
        }

        // Apply the appropriate date range for the new view if it hasn't been customized
        this.applyDefaultDateRangeForView(newValue)

        // Check if we need to re-fetch data due to date range change
        if (oldValue && this.shouldRefetchForTabChange(oldValue, newValue)) {
          await this.fetchTimeData()
        }
      },
      immediate: true,
    },
    timeShift: {
      async handler(newValue, oldValue) {
        // Only fetch new data if we're in time-entry view and timeShift actually changed
        if (this.showTimeEntryList === 'time-entry' && newValue !== oldValue && oldValue !== undefined) {
          await this.fetchTimeData()
        }
      },
    },
    '$route.query.week': {
      immediate: true,
      handler(newWeek, oldWeek) {
        // Sync store when URL changes (browser back/forward, direct URL access)
        if (newWeek !== oldWeek) {
          const parsedShift = newWeek ? parseInt(newWeek, 10) : 0
          if (!Number.isNaN(parsedShift)) {
            // Update store to match URL (don't trigger URL update again)
            // Check if internal module is registered before committing
            if (this.$store.hasModule && this.$store.hasModule('internal')) {
              this.$store.commit('internal/timeShift', parsedShift)
            }
          }
        }
      },
    },
    '$route.query': {
      async handler(newQuery, oldQuery) {
        // Watch for changes in startDate/endDate parameters
        const oldStartDate = oldQuery?.startDate
        const oldEndDate = oldQuery?.endDate
        const newStartDate = newQuery?.startDate
        const newEndDate = newQuery?.endDate

        // If startDate or endDate changed, refetch data for time-entry view
        if (this.showTimeEntryList === 'time-entry'
            && (oldStartDate !== newStartDate || oldEndDate !== newEndDate)) {
          await this.fetchTimeData()
        }
      },
      deep: true,
    },
    // Watch for time modal closing to refresh data only if submission was successful
    '$store.state.internal.isTimeModalVisible': {
      async handler(isVisible, wasVisible) {
        // When modal closes (was visible, now not visible), check if a submission occurred
        if (wasVisible && !isVisible) {
          const wasSubmitted = this.$store.state.internal.timeEntrySubmitted

          if (wasSubmitted) {
            // Reset the flag and refetch data
            this.$store.commit('internal/timeEntrySubmitted', false)
            await this.fetchTimeData()
          }
        }
      },
    },
  },
  async created() {
    // Initialize activeTab based on showTimeEntryList
    this.activeTab = this.tabIndex

    // Initialize timeShift from URL query parameter on first load
    const queryWeek = this.$route.query.week
    if (queryWeek !== undefined) {
      const parsedShift = parseInt(queryWeek, 10)
      if (!Number.isNaN(parsedShift)) {
        // Sync store with URL parameter
        // Check if internal module is registered before committing
        if (this.$store.hasModule && this.$store.hasModule('internal')) {
          this.$store.commit('internal/timeShift', parsedShift)
        }
      }
    }

    // Fetch all required data
    await Promise.all([
      this.fetchTimeData(),
      this.getProjects(),
      this.getServices(),
    ])

    // Load saved templates from localStorage
    this.loadTemplates()
  },
  mounted() {
    // Initialize person filter to current user's personId
    const currentUser = this.$store.state.auth.user
    if (currentUser && currentUser.personId) {
      this.selectedPersonFilter = [currentUser.personId]
    }

    // Add keyboard shortcuts
    window.addEventListener('keydown', this.handleKeyboardShortcut)
  },
  beforeDestroy() {
    // Remove keyboard shortcuts
    window.removeEventListener('keydown', this.handleKeyboardShortcut)
  },
  methods: {
    /**
     * Export table data by calling the DataTable's export methods
     * @param {String} exportFormat - Export format ('csv' or 'xlsx')
     */
    exportTableData(exportFormat) {
      const { table } = this.$refs
      if (table) {
        if (exportFormat === 'csv') {
          table.exportAsCsv()
        } else if (exportFormat === 'xlsx') {
          table.exportAsXlsx()
        }
      }
    },

    /**
     * Handle keyboard shortcuts for navigation and quick actions
     * @param {KeyboardEvent} event - Keyboard event
     */
    handleKeyboardShortcut(event) {
      // Only handle shortcuts in time-entry view
      if (this.showTimeEntryList !== 'time-entry') return

      // Don't trigger shortcuts if user is typing in an input field
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

      // Arrow keys for week navigation
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.timeShiftLeft()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.timeShiftRight()
      } else if (event.key === 'n' && !event.ctrlKey && !event.metaKey) {
        // 'n' key for new entry (today)
        event.preventDefault()
        this.openTimeEntryFormCreate(this.today)
      } else if (event.key === 't' && !event.ctrlKey && !event.metaKey) {
        // 't' key for go to today
        event.preventDefault()
        this.timeShift = 0
      } else if (event.key === 'Escape' && this.selectedEntries.length > 0) {
        // Escape to clear selection
        event.preventDefault()
        this.clearSelection()
      }
    },

    /**
     * Helper method to get filtered times for a specific tab
     * Used for calculating tab counts
     * @param {String} tabId - The tab identifier
     * @returns {Array} Filtered time entries for the tab
     */
    filteredTimesForTab(tabId) {
      const allTimes = this.times || []

      // Get the date range for the tab
      let dateRange
      if (tabId === 'time-entry') {
        // For time-entry, use the current week or custom range
        const timeShiftValue = this.timeShift !== null ? this.timeShift : 0
        const week = dates.getWeek(timeShiftValue)
        dateRange = [dates.getFormattedDate(week[0]), dates.getFormattedDate(week[6])]
      } else {
        // For other tabs, use their configured date range
        dateRange = this.dateRanges[tabId] || this.dateRanges.mtd
      }

      // Filter entries by date range
      const [startDate, endDate] = dateRange
      const start = dates.get(startDate)
      const end = dates.get(endDate)

      return allTimes.filter((entry) => {
        const entryDate = dates.get(entry.date)
        return entryDate.isSameOrAfter(start, 'day') && entryDate.isSameOrBefore(end, 'day')
      })
    },

    // Template management methods
    loadTemplates() {
      const stored = localStorage.getItem('timeEntryTemplates')
      if (stored) {
        try {
          this.entryTemplates = JSON.parse(stored)
        } catch (error) {
          this.entryTemplates = []
        }
      }
    },

    saveTemplate(entry) {
      if (!this.templateName || !this.templateName.trim()) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Please enter a template name',
          status: 'error',
        }, { root: true })
        return
      }

      const template = {
        id: Date.now(),
        name: this.templateName.trim(),
        clientId: entry.client_id,
        clientName: entry.clientName,
        projectId: entry.project_id,
        projectName: entry.projectName,
        timeClassificationId: entry.time_classification_id,
        timeClassificationName: entry.timeClassificationName,
        duration: entry.duration,
        description: entry.description,
      }

      this.entryTemplates.push(template)
      localStorage.setItem('timeEntryTemplates', JSON.stringify(this.entryTemplates))

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', {
        message: `Template "${template.name}" saved successfully`,
        status: 'success',
      }, { root: true })

      this.templateName = ''
      this.templateDialog = false
    },

    deleteTemplate(templateId) {
      this.entryTemplates = this.entryTemplates.filter((t) => t.id !== templateId)
      localStorage.setItem('timeEntryTemplates', JSON.stringify(this.entryTemplates))

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', {
        message: 'Template deleted',
        status: 'success',
      }, { root: true })
    },

    applyTemplate(template) {
      const entry = {
        time_entry_date: dates.getFormattedDate(dates.now()),
        timeEntryDate: dates.now(),
        client_id: template.clientId,
        clientName: template.clientName,
        project_id: template.projectId,
        projectName: template.projectName,
        time_classification_id: template.timeClassificationId,
        timeClassificationName: template.timeClassificationName,
        duration: template.duration,
        description: template.description,
      }

      this.openTimeEntryFormCreate(entry)
    },

    /**
     * Get person display name by personId
     * @param {Number} personId - Person ID to lookup
     * @returns {String} Person display name or email
     */
    getPersonDisplayName(personId) {
      const allPersons = this.persons || []
      const person = allPersons.find((p) => p.personId === personId)
      if (person) {
        return person.displayName || person.email || `${person.firstName} ${person.lastName}` || `Person ${personId}`
      }
      return `Person ${personId}`
    },

    /**
     * Get color for time type chip based on classification
     * @param {String} timeType - Time classification name
     * @returns {String} Color hex code for the chip
     */
    getTimeTypeColor(timeType) {
      const colorMap = {
        BILLABLE: '#055289', // darkestblue
        'NON-BILLABLE': '#0094ff', // brightblue
        PROJECT: '#007dd6', // blue
        ADX: '#647b8d', // gray
        'ADX-PG': '#5e6769', // coolgray
      }
      return colorMap[timeType] || '#9e9e9e' // vuetifygray
    },

    /**
     * Get text color for time type chip
     * @returns {String} Text color for the chip
     */
    getTimeTypeTextColor() {
      // All colors in the monochromatic scheme look best with white text
      return 'white'
    },

    /**
     * Filter entries based on search term and quick filters
     * @param {Array} entries - Array of time entries to filter
     * @returns {Array} Filtered entries
     */
    getFilteredEntries(entries) {
      let filtered = entries

      // Apply quick filters first
      if (this.selectedPersonFilter && this.selectedPersonFilter.length > 0) {
        filtered = filtered.filter((entry) => this.selectedPersonFilter.includes(entry.personId))
      }

      if (this.selectedTimeTypeFilter) {
        filtered = filtered.filter((entry) => entry.timeClassificationName === this.selectedTimeTypeFilter)
      }

      if (this.selectedClientFilter) {
        filtered = filtered.filter((entry) => entry.clientName === this.selectedClientFilter)
      }

      if (this.selectedProjectFilter) {
        filtered = filtered.filter((entry) => entry.projectName === this.selectedProjectFilter)
      }

      // Apply search filter
      if (this.tableSearch && this.tableSearch.trim() !== '') {
        const searchTerm = this.tableSearch.toLowerCase().trim()

        filtered = filtered.filter((entry) => {
          // Search across multiple fields
          const searchableFields = [
            entry.billingCode,
            entry.clientName,
            entry.projectName,
            entry.timeClassificationName,
            entry.description,
          ]

          return searchableFields.some((field) => field && field.toString().toLowerCase().includes(searchTerm))
        })
      }

      return filtered
    },

    /**
     * Clear all active quick filters
     */
    clearAllFilters() {
      // Reset person filter to current user only
      const currentUser = this.$store.state.auth.user
      this.selectedPersonFilter = currentUser && currentUser.personId ? [currentUser.personId] : []
      this.selectedTimeTypeFilter = null
      this.selectedClientFilter = null
      this.selectedProjectFilter = null
      this.tableSearch = ''
      this.filterMenuOpen = false
    },

    /**
     * Clean URL query parameters by keeping only recognized parameters
     * @param {Object} currentQuery - Current route query object
     * @returns {Object} Cleaned query object with only valid parameters
     */
    cleanQueryParameters(currentQuery) {
      // Define the whitelist of recognized query parameters for the Time component
      const validParameters = ['report', 'week', 'startDate', 'endDate']

      const cleanedQuery = {}

      // Only keep parameters that are in our whitelist
      validParameters.forEach((param) => {
        if (currentQuery[param] !== undefined) {
          cleanedQuery[param] = currentQuery[param]
        }
      })

      return cleanedQuery
    },

    async fetchTimeData() {
      this.loading = true
      this.error = null

      try {
        // Initialize response variable to hold API response.
        let response

        // Get accessible persons.
        const hasAccessToOrgTimeEntries = this.$store.getters['auth/hasAccessToOrgTimeEntries']

        const accessiblePersons = await this.getAccessiblePersons()

        // Use the current view's date range
        const currentRange = this.currentDateRange
        const startDate = dates.get(currentRange[0])
        const endDate = dates.get(currentRange[1])

        // If user has org-wide access, fetch time entries by date range.
        // Otherwise, fetch time entries for accessible persons.
        if (hasAccessToOrgTimeEntries) {
          // Different API calls based on active tab
          // 📝 To add new tabs with custom API logic, see docs/time-tabs-quick-reference.md

          const currentActiveTab = this.activeTabMap[this.activeTab] || 'time-entry'

          // Fetch entries for all accessible persons as dictated by the API.
          if (currentActiveTab === 'time-entry') {
            // For Time Entry, get entries for self, direct reports, and org-wide
            // entries within the given date range.
            response = await api.viewTimeEntriesByDateRange([startDate, endDate])
          } else if (currentActiveTab === 'mtd') {
            // For MTD, use the current date range.
            response = await api.viewTimeEntriesByDateRange([startDate, endDate])
          } else if (currentActiveTab === 'billable') {
            // For Billable tab, use the current date range
            // and set the timeClassificationName filter to "Billable".
            response = await api.viewTimeEntriesByDateRange([startDate, endDate], { timeClassificationName: 'BILLABLE' })
          } else if (currentActiveTab === 'projects') {
            // For Projects tab, use the current date range.
            response = await api.viewTimeEntriesByDateRange([startDate, endDate])
          } else {
            response = await api.viewTimeEntriesByDateRange([startDate, endDate])
          }
        } else {
          const personIds = accessiblePersons.map((person) => {
            const isNumber = typeof person.personId === 'number'
            return (isNumber && person.personId) || null
          }).join(',')

          if (personIds) {
            // Use the same API call pattern as the original - multiple person IDs
            response = await api.viewTimeEntriesByPersonId(personIds, [startDate, endDate])
          } else {
            // Fallback to just current user if no persons data available
            const { personId } = this.$store.state.auth.user

            response = await api.viewTimeEntriesByPersonId(personId, [startDate, endDate])
          }
        }

        if (response.error) {
          throw new Error(response.error)
        }

        // Transform and store the data
        this.times = this.transformTimeEntries(response.content || [])

        // Show success message with date range
        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Loaded ${this.filteredTimes.length} time entries (${currentRange[0]} to ${currentRange[1]}) for ${accessiblePersons.length} persons`,
          status: 'success',
        }, { root: true })
      } catch (error) {
        this.error = error.message

        // Show error message
        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Error loading time entries: ${error.message}`,
          status: 'error',
        }, { root: true })
      } finally {
        this.loading = false
      }
    },

    async getAccessiblePersons() {
      // Replicate the delegates getter logic from internal store
      const currentUser = this.$store.getters['auth/currentUser']
      const hasAccessToOrgTimeEntries = this.$store.getters['auth/hasAccessToOrgTimeEntries']

      if (!currentUser) {
        return []
      }

      const { personId: currentPersonId } = currentUser
      const allPersons = this.persons || []

      if (!allPersons.length) {
        // Fallback to just current user if no persons data
        return [{ ...currentUser }]
      }

      return allPersons.reduce((delegates, person) => {
        const isSelf = person.personId === currentPersonId
        const isManager = person.managerPersonId === currentPersonId

        if (isSelf || isManager || hasAccessToOrgTimeEntries) {
          delegates.push({ ...person })
        }

        return delegates
      }, [])
    },

    transformTimeEntries(rawEntries) {
      // Defensive check for rawEntries
      if (!rawEntries) {
        return []
      }

      // Handle case where rawEntries might be an object with a content property
      let entries = rawEntries

      if (rawEntries.content && Array.isArray(rawEntries.content)) {
        entries = rawEntries.content
      }

      // Final safety check - ensure we have an array
      if (!Array.isArray(entries)) {
        // If it's still not an array, log what we got and return empty array
        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Warning: Expected array but got ${typeof entries}`,
          status: 'warning',
        }, { root: true })
        return []
      }

      // Transform the raw API response to match expected format
      // This follows the same pattern as tools/transformTimeItems
      return entries.map((entry) => {
        // Base transformation - copy all original properties
        const transformedEntry = {
          ...entry,
          // Parse date string as local date-only (no time/timezone interpretation)
          // API returns dates like "2025-10-13T00:00:00" which should be treated as local midnight
          // Extract just the date part (YYYY-MM-DD) to avoid any timezone issues
          timeEntryDate: entry.timeEntryDate
            ? dayjs(entry.timeEntryDate.split('T')[0]).startOf('day')
            : null,
          // Map API response fields to expected internal fields
          time_entry_date: entry.timeEntryDate,
          person_id: entry.personId,
          client_id: entry.clientId,
          time_classification_id: entry.timeClassificationId,
          duration: entry.billableActionDuration || entry.duration || 0,
          person_email: entry.personEmail || null,
          description: entry.workNote || entry.description,
          project_id: entry.projectId,
          service_id: entry.serviceId,
        }

        // Add the formatted fields that DataTable expects
        transformedEntry.hhmm = format.hhmm(transformedEntry.duration)
        transformedEntry.timeEntryDateFormatted = transformedEntry.timeEntryDate
          ? format.date(transformedEntry.timeEntryDate, 'MM/DD/YYYY')
          : ''

        return transformedEntry
      })
    },

    setShowTimeEntryList(layout) {
      if (layout !== this.showTimeEntryList) {
        this.showTimeEntryList = layout
      }
    },

    getSummarySubtitle() {
      const currentTab = this.showTimeEntryList

      if (currentTab === 'time-entry') {
        // For custom timeShift text, show that text directly.
        if (this.customTimeShiftText.includes(this.timeShift)) {
          return this.timeShiftText
        }

        // For other timeShift values, show the week range.
        return this.getWeekDateRange
      }

      const tabsWithDateRanges = ['mtd', 'billable', 'projects']

      if (tabsWithDateRanges.includes(currentTab)) {
        return this.getWeekDateRange
      }

      // Default to empty subtitle.
      return ''
    },

    getDefaultDateRangeForView(view) {
      // Define default date ranges for each view type
      // 📅 To add new date ranges, see docs/time-tabs-quick-reference.md
      const dateRangeMap = {
        'time-entry': (() => {
          // Current week range for time entry view (weekly calendar)
          const week = dates.getWeek(0) // Current week
          const startOfWeek = week[0] // Monday
          const endOfWeek = week[6] // Sunday
          return [dates.getFormattedDate(startOfWeek), dates.getFormattedDate(endOfWeek)]
        })(),
        mtd: dates.mtdFormatted(),
        billable: dates.mtdFormatted(),
        projects: dates.mtdFormatted(),
        // Future examples:
        // 'last-month': dates.lastMonthFormatted(),
        // 'ytd': dates.ytdFormatted(),
        // 'custom-range': dates.customRangeFormatted(),
      }

      return dateRangeMap[view] || dates.mtdFormatted()
    },

    applyDefaultDateRangeForView(view) {
      // Only apply default if the user hasn't set a custom date range for this view
      if (!this.dateRanges[view]) {
        this.dateRanges[view] = this.getDefaultDateRangeForView(view)
      }
    },

    shouldRefetchForTabChange(oldTab, newTab) {
      // Always refetch if switching to a different tab
      if (newTab !== oldTab) {
        return true
      }

      // Get date ranges for both tabs
      const oldRange = this.dateRanges[oldTab] || this.getDefaultDateRangeForView(oldTab)
      const newRange = this.dateRanges[newTab] || this.getDefaultDateRangeForView(newTab)

      // Compare date ranges - refetch only if they're different
      const rangesAreDifferent = oldRange[0] !== newRange[0] || oldRange[1] !== newRange[1]

      return rangesAreDifferent
    },

    // Helper method for adding new date range types in the future
    addCustomDateRangeType(tabName, dateRangeFunction) {
      // Example usage: this.addCustomDateRangeType('last-month', dates.lastMonthFormatted)
      // This would allow easy addition of new tabs with custom date ranges
      this.dateRanges = {
        ...this.dateRanges,
        [tabName]: dateRangeFunction(),
      }
    },

    openTimeEntryFormCreate(weekdate) {
      return this.$store.dispatch('internal/openTimeEntryFormCreate', weekdate)
    },
    openTimeEntryFormUpdate(entry) {
      return this.$store.dispatch('internal/openTimeEntryFormUpdate', entry)
    },
    openTimeEntryFormDuplicate(entry, dateValue) {
      // Create a duplicate entry with the same data but for the current date
      const duplicateEntry = {
        ...entry,
        timeEntryId: null, // Remove ID so it creates a new entry
        time_entry_date: dates.getFormattedDate(dateValue || dates.now()),
        timeEntryDate: dateValue || dates.now(),
      }
      return this.$store.dispatch('internal/openTimeEntryFormCreate', duplicateEntry)
    },
    openTimeEntryFormDelete(entry) {
      return this.$store.dispatch('internal/openTimeEntryFormDelete', entry)
    },

    // Bulk operation methods
    isEntrySelected(entryId) {
      return this.selectedEntries.includes(entryId)
    },

    toggleEntrySelection(entryId, selected) {
      if (selected) {
        if (!this.selectedEntries.includes(entryId)) {
          this.selectedEntries.push(entryId)
        }
      } else {
        this.selectedEntries = this.selectedEntries.filter((id) => id !== entryId)
      }
    },

    areAllDayEntriesSelected(entries) {
      const filteredEntries = this.getFilteredEntries(entries)
      if (filteredEntries.length === 0) return false
      return filteredEntries.every((entry) => this.selectedEntries.includes(entry.timeEntryId))
    },

    areSomeDayEntriesSelected(entries) {
      const filteredEntries = this.getFilteredEntries(entries)
      return filteredEntries.some((entry) => this.selectedEntries.includes(entry.timeEntryId))
    },

    toggleDaySelection(entries, selected) {
      const filteredEntries = this.getFilteredEntries(entries)
      if (selected) {
        // Add all entries from this day
        filteredEntries.forEach((entry) => {
          if (!this.selectedEntries.includes(entry.timeEntryId)) {
            this.selectedEntries.push(entry.timeEntryId)
          }
        })
      } else {
        // Remove all entries from this day
        const entryIds = filteredEntries.map((e) => e.timeEntryId)
        this.selectedEntries = this.selectedEntries.filter((id) => !entryIds.includes(id))
      }
    },

    clearSelection() {
      this.selectedEntries = []
    },

    openBulkDeleteDialog() {
      this.bulkOperationType = 'delete'
      this.bulkOperationDialog = true
    },

    openBulkCopyDialog() {
      this.bulkOperationType = 'copy'
      this.copyToDate = dates.getFormattedDate(dates.now())
      this.bulkOperationDialog = true
    },

    async confirmBulkOperation() {
      if (this.bulkOperationType === 'delete') {
        await this.bulkDeleteEntries()
      } else if (this.bulkOperationType === 'copy') {
        await this.bulkCopyEntries()
      }
      this.bulkOperationDialog = false
      this.clearSelection()
    },

    async bulkDeleteEntries() {
      const entriesToDelete = this.times.filter((entry) => this.selectedEntries.includes(entry.timeEntryId))

      try {
        // Delete each entry in parallel
        await Promise.all(
          entriesToDelete.map((entry) => this.$store.dispatch('internal/openTimeEntryFormDelete', entry)),
        )

        // Refresh data
        await this.fetchTimeData()

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Successfully deleted ${entriesToDelete.length} ${entriesToDelete.length === 1 ? 'entry' : 'entries'}`,
          status: 'success',
        }, { root: true })
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Error deleting entries: ${error.message}`,
          status: 'error',
        }, { root: true })
      }
    },

    async bulkCopyEntries() {
      const entriesToCopy = this.times.filter((entry) => this.selectedEntries.includes(entry.timeEntryId))

      try {
        // Copy each entry to the new date in parallel
        await Promise.all(
          entriesToCopy.map((entry) => {
            const duplicateEntry = {
              ...entry,
              timeEntryId: null,
              time_entry_date: this.copyToDate,
              timeEntryDate: dates.get(this.copyToDate),
            }
            return this.$store.dispatch('internal/openTimeEntryFormCreate', duplicateEntry)
          }),
        )

        // Refresh data
        await this.fetchTimeData()

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Successfully copied ${entriesToCopy.length} ${entriesToCopy.length === 1 ? 'entry' : 'entries'} to ${this.copyToDate}`,
          status: 'success',
        }, { root: true })
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Error copying entries: ${error.message}`,
          status: 'error',
        }, { root: true })
      }
    },

    async timeShiftLeft() {
      // Clear any custom date range for time-entry to use timeShift-based navigation
      if (this.showTimeEntryList === 'time-entry' && this.hasCustomTimeEntryRange) {
        delete this.dateRanges['time-entry']
      }

      // Get current timeShift value (handle null case for custom ranges)
      const currentShift = this.timeShift !== null ? this.timeShift : 0

      // Use the computed property setter to update both store and URL
      this.timeShift = currentShift - 1

      // Fetch new week data when in time-entry view
      if (this.showTimeEntryList === 'time-entry') {
        await this.fetchTimeData()
      }
    },
    async timeShiftRight() {
      // Clear any custom date range for time-entry to use timeShift-based navigation
      if (this.showTimeEntryList === 'time-entry' && this.hasCustomTimeEntryRange) {
        delete this.dateRanges['time-entry']
      }

      // Get current timeShift value (handle null case for custom ranges)
      const currentShift = this.timeShift !== null ? this.timeShift : 0

      // Use the computed property setter to update both store and URL
      this.timeShift = currentShift + 1

      // Fetch new week data when in time-entry view
      if (this.showTimeEntryList === 'time-entry') {
        await this.fetchTimeData()
      }
    },

    async onApplyDateRange(dateRange) {
      this.applyingDateFilter = true

      try {
        const currentView = this.showTimeEntryList

        if (currentView === 'time-entry') {
          // For time-entry view, check if this matches a standard Monday-Sunday week
          const startDate = dates.get(dateRange[0])
          const endDate = dates.get(dateRange[1])

          // Quick check: is this a Monday to Sunday range?
          const isMonday = startDate.day() === 1 // Monday = 1 in dayjs
          const isSunday = endDate.day() === 0 // Sunday = 0 in dayjs
          const daysDiff = endDate.diff(startDate, 'days')
          const isExactWeek = isMonday && isSunday && daysDiff === 6

          if (isExactWeek) {
            // Calculate the timeShift for this exact week
            const currentWeek = dates.getWeek(0) // Current week
            const weeksDiff = Math.round(startDate.diff(currentWeek[0], 'weeks', true))

            // Use timeShift navigation for standard weeks
            this.timeShift = weeksDiff
          } else {
            // Custom date range - use URL parameters for precise bookmarking
            const cleanedQuery = this.cleanQueryParameters(this.$route.query)

            // Clear timeShift parameter and set custom range
            delete cleanedQuery.week
            cleanedQuery.startDate = dates.getFormattedDate(dateRange[0])
            cleanedQuery.endDate = dates.getFormattedDate(dateRange[1])

            // Update URL to reflect custom date range
            this.$router.push({ query: cleanedQuery })
          }
        } else {
          // For other views, store the date range normally
          this.dateRanges[currentView] = [
            dates.getFormattedDate(dateRange[0]),
            dates.getFormattedDate(dateRange[1]),
          ]
        }

        // Re-fetch data with new date range
        await this.fetchTimeData()
      } catch (error) {
        // Error handling is done in fetchTimeData
        // Silently continue - error display handled in fetchTimeData
      } finally {
        this.applyingDateFilter = false
      }
    },

    async onClearDateRange() {
      this.applyingDateFilter = true

      try {
        const currentView = this.showTimeEntryList

        if (currentView === 'time-entry') {
          // For time-entry view, clear any custom date parameters and reset to current week
          const cleanedQuery = this.cleanQueryParameters(this.$route.query)
          delete cleanedQuery.startDate
          delete cleanedQuery.endDate
          delete cleanedQuery.week // Remove week param to default to current week

          // Update URL and store
          this.$router.push({ query: cleanedQuery })
          if (this.$store.hasModule && this.$store.hasModule('internal')) {
            this.$store.commit('internal/timeShift', 0)
          }
        } else {
          // For other views, reset the date range to its default
          this.dateRanges[currentView] = this.getDefaultDateRangeForView(currentView)
        }

        // Re-fetch data with default date range
        await this.fetchTimeData()
      } catch (error) {
        // Error handling is done in fetchTimeData
        // Silently continue - error display handled in fetchTimeData
      } finally {
        this.applyingDateFilter = false
      }
    },

    sameMonthText: (monday, sunday) => `${dates.getFormattedDate(monday, 'MMM DD')}-${dates.getFormattedDate(sunday, 'DD, YYYY')}`,
    crossMonthText: (monday = {}, sunday = {}) => {
      const { $y: startYear } = monday
      const { $y: endYear } = sunday
      const sameYear = startYear === endYear
      const startFormat = sameYear ? 'MMM DD' : 'MMM DD, YYYY'
      const endFormat = sameYear ? 'DD, YYYY' : 'MMM DD, YYYY'

      return `${dates.getFormattedDate(monday, startFormat)}-${dates.getFormattedDate(sunday, endFormat)}`
    },

    getEntriesForDay(weekdate) {
      // Normalize both dates to start of day in local timezone before comparing
      // This ensures we're doing a date-only comparison regardless of time components
      const matchDate = (entry) => {
        if (!entry || !entry.timeEntryDate || !weekdate) return false

        // Ensure both have the startOf method (are dayjs objects)
        if (typeof entry.timeEntryDate.startOf !== 'function' || typeof weekdate.startOf !== 'function') {
          return false
        }

        // Normalize both to start of day and compare timestamps
        const entryStartOfDay = entry.timeEntryDate.startOf('day').valueOf()
        const weekdateStartOfDay = weekdate.startOf('day').valueOf()

        return entryStartOfDay === weekdateStartOfDay
      }
      return this.ownTimes.filter(matchDate)
    },
    getAllEntriesFromCurrentWeek() {
      return this.currentWeek.reduce((entries, day) => {
        entries = entries.concat(day.entries)

        return entries
      }, [])
    },

    getAllEntriesFromTimeView() {
      // Get all entries from the current week for the time-entry view
      if (this.currentWeek && this.currentWeek.week && Array.isArray(this.currentWeek.week)) {
        return this.currentWeek.week.reduce((entries, day) => {
          entries = entries.concat(day.entries || [])
          return entries
        }, [])
      }
      return []
    },

    /**
     * @function setEntryClass
     * @desc Determines the class for a week's date entry based on
     * dark mode, locked status, and index in the week.
     * @param weekdate - moment date object
     * @param index - index of the entry in the week
     */
    setEntryClass(weekdate, index) {
      const { inDarkMode, lockedClass, lockedDates } = this
      const color = inDarkMode === true ? 'vuetifyblack' : 'white'

      const calendarDate = weekdate.date.valueOf()
      const isLocked = lockedDates.includes(calendarDate)
      const colored = isLocked ? lockedClass : color
      const uncolored = isLocked ? lockedClass : color
      const striping = index % 2 === 0 ? colored : uncolored

      return striping
    },

    durationFormatter(duration) {
      return durations.durationFormatter(duration)
    },

    /**
     * Get color for time classification chip
     * @param {String} classification - Time classification name
     * @returns {String} Vuetify color name
     */
    getTimeClassificationColor(classification) {
      const colors = {
        BILLABLE: 'success',
        'NON-BILLABLE': 'primary',
        PROJECT: 'info',
        ADX: 'grey darken-1',
        'ADX-PG': 'grey darken-2',
      }
      return colors[classification] || 'grey'
    },

    /**
     * Format date for display
     * @param {String|Number} date - Date to format
     * @returns {String} Formatted date string
     */
    formatDate(date) {
      if (!date) return 'N/A'
      return dates.formatDate(date, 'MM/DD/YYYY')
    },

    getTotal(entries) {
      const seed = 0

      return entries.reduce((total, entry) => {
        total += entry.duration

        return total
      }, seed)
    },

    async getProjects() {
      // Fetch projects if needed.
      if (!this.projectsResponse || this.projectsResponse.length === 0) {
        const apiResponse = await api.getProjects()
        const { content, error } = apiResponse
        const isSuccessResponse = Array.isArray(content)
        const foundError = !isSuccessResponse ? error || content : error

        if (foundError) {
          return { error: foundError }
        }

        const projects = isSuccessResponse ? content : []

        this.$store.commit('tools/projectsResponse', projects)
      }

      return Promise.resolve(this.servicesResponse)
    },

    async getServices() {
      // Fetch services if needed.
      if (!this.servicesResponse || this.servicesResponse.length === 0) {
        const apiResponse = await api.getServices()
        const { content, error } = apiResponse
        const isSuccessResponse = Array.isArray(content)
        const foundError = !isSuccessResponse ? error || content : error

        if (foundError) {
          return { error: foundError }
        }

        const services = isSuccessResponse ? content : []

        this.$store.commit('tools/servicesResponse', services)
      }

      return Promise.resolve(this.servicesResponse)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

// Enhanced summary cards styling
.summary-cards-container {
  .summary-card-col {
    display: flex;
    flex-direction: column;
  }

  .summary-card {
    height: 100%;
    transition: all 0.3s ease;
    border-radius: 12px !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
    }

    .summary-title {
      min-height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .summary-subtitle {
      min-height: 20px;
      line-height: 1.2;
    }

    .summary-value {
      line-height: 1.1;
      letter-spacing: -0.5px;
    }
  }
}

.hours-label {
  font-size: small;
  margin-left: 1ch;
}

.daily-total {
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 500;
}

.search-field {
  ::v-deep .v-input__control {
    min-height: auto;
  }

  ::v-deep .v-text-field__details {
    display: none;
  }
}

.quick-filter-select {
  ::v-deep .v-input__control {
    min-height: auto;
  }

  ::v-deep .v-text-field__details {
    display: none;
  }

  &.compact {
    ::v-deep .v-select__selection {
      font-size: 0.875rem;
    }
  }
}

.clear-filters-btn {
  white-space: nowrap;
  min-width: auto !important;
  padding: 0 8px !important;
}

.filter-active {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-color: var(--v-primary-base) !important;
}

.v-data-table {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.08);

  // General styling (less specific, must come first)
  .v-btn {
    margin-top: -3px;
  }

  ::v-deep th {
    font-size: 13px;
    font-weight: bold !important;
    word-wrap: break-word;
    word-break: break-word;
    background-color: #f8f9fa !important;
    border-bottom: 2px solid rgba(0, 0, 0, 0.12) !important;
    color: #424242 !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 16px !important;
  }

  ::v-deep td {
    font-size: 13px;
    word-wrap: break-word;
    word-break: break-word;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 12px 16px !important;
  }

  // Enhanced table styling (less specific tbody tr, must come first)
  &.enhanced-table {
    // Row hover effects
    ::v-deep tbody tr {
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background-color: rgba(25, 118, 210, 0.08) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }
    }
  }

  // Striped table styling (more specific nth-of-type, comes after enhanced)
  &.striped-table {
    ::v-deep tbody tr:nth-of-type(odd) {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  // Entry actions styling (more specific button rules)
  .entry-actions {
    min-width: 160px;
    display: flex;
    justify-content: flex-end;
    gap: 4px;

    .v-btn {
      transition: all 0.2s ease;
    }
  }

  // Time type chip styling
  .time-type-chip {
    font-weight: 600;
    font-size: 12px;
  }

  // Dark mode styling (most specific, comes last)
  .theme--dark & {
    border: 1px solid rgba(255, 255, 255, 0.12);

    ::v-deep th {
      background-color: #2d2d2d !important;
      border-bottom: 2px solid rgba(255, 255, 255, 0.2) !important;
      color: #f5f5f5 !important; // Increased contrast from #e0e0e0 to #f5f5f5
    }

    ::v-deep td {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    &.enhanced-table ::v-deep tbody tr:hover {
      background-color: rgba(25, 118, 210, 0.15) !important;
    }

    &.striped-table ::v-deep tbody tr:nth-of-type(odd) {
      background-color: rgba(255, 255, 255, 0.03);
    }
  }
}

// Clock icon sizing in card headers
.clock-icon-12px {
  font-size: 12px !important;
}

// ============================================================================
// LIST VIEW CARD STYLING (MTD, Billable, Projects tabs) - Less specific, comes first
// ============================================================================

.time-entry-card-list {
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }

  .icon-fixed-width {
    width: 16px;
    color: rgba(0, 0, 0, 0.54);
  }

  .v-card__title {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .v-card__text {
    flex-grow: 1;

    .body-2 {
      line-height: 1.4;
    }
  }

  .v-card__actions {
    margin-top: auto;
  }
}

// ============================================================================
// CALENDAR VIEW CARD STYLING (Time Entry tab) - More specific, comes after
// ============================================================================

.time-entry-card {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
    border-color: rgba(var(--v-primary-base), 0.4);
  }

  &.entry-selected {
    border-color: var(--v-primary-base);
    border-width: 2px;
    background-color: rgba(var(--v-primary-base), 0.04);
  }

  &.entry-locked {
    opacity: 0.6;
    background-color: rgba(0, 0, 0, 0.02);
  }

  .v-card__title {
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.02), transparent);
    min-height: 56px;
  }

  .v-card__text {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
    flex-grow: 1;
  }

  // Time type chip in header - subtle outlined style
  .time-type-chip-header {
    font-size: 0.75rem;
    padding: 0 12px;
    height: 26px;
    letter-spacing: 0.3px;
    font-weight: 500;
    border-width: 1.5px !important;
    background-color: transparent !important;

    // Add subtle background tint on hover
    &:hover {
      opacity: 0.8;
    }
  }

  // Compact version for column calendar view
  .time-type-chip-compact {
    font-size: 0.65rem;
    padding: 0 8px;
    height: 22px;
    letter-spacing: 0.3px;
    font-weight: 500;
    border-width: 1.5px !important;
    background-color: transparent !important;

    &:hover {
      opacity: 0.8;
    }
  }

  // Icon-based info row styling
  .entry-info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    line-height: 1.5;

    .icon-fixed-width {
      width: 16px;
      margin-top: 3px;
      flex-shrink: 0;
    }

    .entry-info-content {
      flex: 1;
      color: rgba(0, 0, 0, 0.87);

      // Ensure appropriate font weights
      .body-2 {
        font-weight: 500; // Medium weight for all body-2 text in info rows

        &.font-weight-bold {
          font-weight: 700; // Bold for emphasis
        }

        &.font-weight-medium {
          font-weight: 500; // Medium weight
        }
      }

      .caption {
        font-weight: 500; // Medium weight for captions

        &.font-weight-medium {
          font-weight: 500;
        }
      }
    }
  }

  // Notes display styling
  .entry-notes-display {
    padding: 10px 12px;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
    border-left: 3px solid rgba(var(--v-primary-base), 0.3);

    .work-notes-text {
      font-style: italic;
      color: rgba(0, 0, 0, 0.65);
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin: 0;
    }
  }

  .entry-detail {
    .detail-label {
      font-weight: 600;
      font-size: 0.7rem;
      letter-spacing: 0.8px;
      margin-bottom: 4px;
      opacity: 0.7;
      color: #5e6769; // coolgray for light mode
    }

    .detail-value {
      color: rgba(0, 0, 0, 0.87);
      word-break: break-word;
      line-height: 1.5;

      &.work-notes-text {
        font-style: italic;
        color: rgba(0, 0, 0, 0.65);
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
  }

  .v-divider {
    margin: 0;
  }

  .v-card__actions {
    margin-top: auto;
    background: rgba(0, 0, 0, 0.02);
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    .v-btn {
      transition: all 0.2s ease;
      // Increased minimum touch target size for accessibility
      min-width: 44px;
      min-height: 44px;
    }

    // Ensure proper spacing for icon groups
    .d-flex.align-center {
      gap: 4px;
    }

    // Subtle action button colors (grey-blue for better brand cohesion)
    .action-btn {
      color: $color-monochrome-actiongray !important;
    }
  }
}

// Action button hover states - must come after base styles for proper specificity
.time-entry-card .v-card__actions .action-btn.action-btn-duplicate:hover:not(:disabled) {
  color: #4caf50 !important; // Green on hover
}

.time-entry-card .v-card__actions .action-btn.action-btn-edit:hover:not(:disabled) {
  color: #1976d2 !important; // Blue on hover
}

.time-entry-card .v-card__actions .action-btn.action-btn-delete:hover:not(:disabled) {
  color: #f44336 !important; // Red on hover
}

// Dark mode and responsive styles for list cards (after calendar card styles for proper specificity)
.theme--dark .time-entry-card-list {
  background: #2d2d2d;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  }

  .icon-fixed-width {
    color: rgba(255, 255, 255, 0.54);
  }

  .v-card__title {
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }
}

// Responsive adjustments for list cards
@media (max-width: 768px) {
  .time-entry-card-list {
    .v-card__title {
      flex-wrap: wrap;
      gap: 8px;
    }
  }
}

// Column Calendar View Styling
.time-entries-columns {
  display: flex;
  gap: 0;
  overflow-x: auto; // Enable horizontal scrolling
  -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS

  .col {
    flex: 1 1 140px; // Allow grow, shrink, with base of 140px
    min-width: 140px; // Ensure minimum readable width
    max-width: none; // Allow columns to expand on larger screens
    padding-left: 0;
    padding-right: 0;
  }

  > .col:not(:last-child) {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }

  > .col:last-child {
    padding-right: 0;
  }

  .day-column-header {
    border-radius: 12px 12px 0 0;
    border-bottom: 3px solid var(--v-primary-base);
    margin-bottom: 0;

    .v-card__title {
      background: linear-gradient(to bottom, rgba(var(--v-primary-base), 0.08), rgba(var(--v-primary-base), 0.02));
      padding: 12px !important;
    }

    .full-width {
      width: 100%;
    }

    // Day column date styling
    .day-column-date {
      color: #055289; // darkestblue for light mode

      &.locked {
        color: #5e6769; // coolgray for locked dates
      }
    }
  }

  .day-column-entries {
    min-height: 200px;
    padding: 4px 8px 4px 8px;
  }

  .time-entry-card-compact {
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    }

    &.entry-selected {
      border-color: var(--v-primary-base);
      border-width: 2px;
      background-color: rgba(var(--v-primary-base), 0.04);
    }

    .card-header-compact {
      min-height: auto;
      padding: 6px 8px !important;
    }

    // Enhanced styling for cleaner card layout
    .entry-duration-display {
      display: flex;
      align-items: center;
      padding: 4px 0;
    }

    .entry-info-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      line-height: 1.4;

      .icon-fixed-width {
        width: 14px;
        margin-top: 2px;
        flex-shrink: 0;
      }

      .entry-info-content {
        flex: 1;
        color: rgba(0, 0, 0, 0.87);

        // Ensure appropriate font weights for compact view
        .body-2 {
          font-weight: 500; // Medium weight

          &.font-weight-bold {
            font-weight: 700;
          }

          &.font-weight-medium {
            font-weight: 500;
          }
        }

        .caption {
          font-weight: 500; // Medium weight for captions

          &.font-weight-medium {
            font-weight: 500;
          }
        }
      }
    }

    .entry-notes-display {
      padding: 8px;
      background-color: rgba(0, 0, 0, 0.02);
      border-radius: 6px;
      border-left: 3px solid rgba(var(--v-primary-base), 0.3);

      .work-notes-text {
        font-style: italic;
        color: rgba(0, 0, 0, 0.65);
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin: 0;
      }
    }

    .entry-detail-compact {
      .detail-label {
        font-weight: 600;
        font-size: 0.65rem;
        letter-spacing: 0.5px;
        margin-bottom: 2px;
        opacity: 0.7;
        color: #5e6769; // coolgray for light mode
      }

      .detail-value {
        color: rgba(0, 0, 0, 0.87);
        word-break: break-word;
        line-height: 1.4;

        &.work-notes-text {
          font-style: italic;
          color: rgba(0, 0, 0, 0.65);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }
    }
  }
}

// Empty state styling
.empty-state-text {
  color: #5e6769; // coolgray for light mode
}

// Enhanced search and filter section styling (higher specificity)
.search-filter-section {
  border-radius: 8px !important;

  .search-field-enhanced {
    ::v-deep .v-input__control {
      min-height: 42px;
      max-height: 42px;
      border-radius: 6px;
      min-width: 180px;
    }

    ::v-deep .v-input__slot {
      padding: 0 12px;
      background: rgba(255, 255, 255, 0.9) !important;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        border-color: rgba(25, 118, 210, 0.4);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }
    }

    ::v-deep .v-text-field__slot input {
      font-weight: 500;
      font-size: 0.875rem;
      padding: 0;
      margin-top: -1px;
      line-height: 1.4;

      &::placeholder {
        color: rgba(0, 0, 0, 0.54);
        font-weight: 400;
        opacity: 1;
      }
    }

    ::v-deep .v-input__icon--prepend-inner {
      margin-top: -1px;
      margin-right: 8px;
      color: rgba(0, 0, 0, 0.54);
    }

    ::v-deep .v-input__icon--append {
      margin-top: -1px;
      color: rgba(0, 0, 0, 0.54);
    }

    // Focus state
    ::v-deep .v-input--is-focused .v-input__slot {
      border-color: var(--v-primary-base) !important;
      box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
    }
  }

  .filter-button-enhanced {
    font-weight: 500;
    letter-spacing: 0.25px;
    height: 36px !important;
    text-transform: none;
    border-radius: 6px;
    min-width: 120px;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
      transition: all 0.2s ease;
    }

    &.filter-active {
      background-color: rgba(25, 118, 210, 0.08) !important;
      border-color: var(--v-primary-base) !important;
      color: var(--v-primary-base) !important;

      .v-chip {
        background-color: var(--v-primary-base) !important;
        color: white !important;
      }
    }
  }

  // Controls container styling
  .controls-container {
    // Desktop: horizontal layout with date picker on the right
    display: flex;
    align-items: center;
    gap: 16px;

    .search-filter-row {
      flex-grow: 1;

      .col {
        display: flex;
        align-items: center;

        &.flex-shrink-0 {
          flex-shrink: 0;
        }

        &.flex-grow-1 {
          flex-grow: 1;
        }
      }
    }

    .date-picker-row {
      flex-shrink: 0;

      .col {
        display: flex;
        align-items: center;
      }
    }
  }
}

// Large screen: max-width for search field
@media (min-width: 1200px) {
  .search-filter-section {
    .search-field-enhanced {
      ::v-deep .v-input__control {
        max-width: 400px;
      }
    }
  }
}

// Tab styling (must come before .time-view-header-compact for specificity)
.time-view-tabs {
  ::v-deep .v-tab {
    text-transform: none;
    font-weight: 500;
  }

  ::v-deep .v-tab--active {
    font-weight: 600;
  }
}

// ============================================================================
// COMPACT HEADER STYLES (Moderate Specificity - Before Dark Theme Overrides)
// ============================================================================

// Remove old header styles (keeping for backward compatibility if needed)
.time-view-header {
  display: none; // Hide if old structure accidentally renders
}

// Compact header styling
.time-view-header-compact {
  .tabs-section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    overflow: hidden; // Ensures children respect border-radius
  }

  .header-toolbar {
    min-height: 56px !important;
    max-height: 56px !important;
    background: white !important;
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(var(--v-primary-base), 0.08);
  }

  .time-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px !important;
      background-color: white !important;
    }

    ::v-deep .v-tab {
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      min-width: 100px;
      padding: 0 16px;
      height: 56px;

      &:hover {
        background-color: rgba(var(--v-primary-base), 0.04);
      }
    }

    ::v-deep .v-tabs-slider {
      height: 3px !important;
    }
  }

  // Tab navigation arrows - extra small
  .tab-nav-arrow {
    font-size: 0.75rem !important; // 12px - smaller than xs
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }

    // Override Vuetify's .v-icon__component sizing
    &.v-slide-group.v-item-group > .v-slide-group__next,
    &.v-slide-group.v-item-group > .v-slide-group__prev {
      height: 14px !important;
      width: 14px !important;
    }
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }

  .theme--dark .time-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      background-color: #2d2d2d !important;
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .add-time-btn-compact {
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
    height: 38px !important;
    padding: 0 20px !important;
    border-radius: 6px;
  }

  .action-buttons-container {
    display: flex;
    align-items: center;
    min-width: 400px; // Reserve space for smooth transitions between states
  }

  .controls-bar {
    border-radius: 0; // No border-radius since it's inside .tabs-section
    margin: 0; // No margin since it's inside .tabs-section

    ::v-deep .v-expand-transition-enter-active,
    ::v-deep .v-expand-transition-leave-active {
      transition: all 0.3s ease;
    }

    .search-field-compact {
      ::v-deep .v-input__control {
        min-height: 38px !important;
      }

      ::v-deep .v-input__slot {
        min-height: 38px !important;
        padding: 0 12px !important;
        border-radius: 6px;
      }

      ::v-deep fieldset {
        border-color: rgba(0, 0, 0, 0.12);
      }

      .search-icon {
        color: rgba(0, 0, 0, 0.54);
        margin-right: 8px;
      }
    }

    .filter-btn-compact {
      text-transform: none;
      letter-spacing: 0.25px;
      font-weight: 500;
      height: 36px !important;

      &.filter-active {
        background-color: rgba(var(--v-primary-base), 0.08);
      }
    }

    // Expandable Filter Section Styles
    .filter-select {
      ::v-deep .v-input__control {
        min-height: 53px !important;
      }

      ::v-deep .v-input__slot {
        min-height: 53px !important;
        padding: 0 12px !important;
        border-radius: 6px;
      }

      ::v-deep fieldset {
        border-color: rgba(0, 0, 0, 0.12);
        border-width: 1px;
      }

      ::v-deep .v-label {
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: 0.25px;
        color: rgba(0, 0, 0, 0.6);
      }

      ::v-deep .v-label--active {
        font-size: 0.75rem;
        font-weight: 600;
      }

      // Font Awesome prepend inner icons
      .filter-icon {
        font-size: 16px;
        color: rgba(0, 0, 0, 0.54);
        margin-top: 2px;
        margin-right: 8px;
      }

      ::v-deep .v-select__selections {
        padding: 0;
        line-height: 1.5;

        input {
          font-size: 0.875rem;
          font-weight: 500;
        }
      }

      ::v-deep .v-select__selection {
        font-size: 0.875rem;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        margin-top: 2px;
      }
    }
  }
}

// Dark theme support for compact header (must come before hover selectors)
.theme--dark .time-view-header-compact {
  .tabs-section {
    background: #2d2d2d;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .header-toolbar {
    background: #2d2d2d !important;
  }

  .header-icon-wrapper {
    background: rgba(var(--v-primary-base), 0.15);
  }

  .controls-bar {
    background: rgba(255, 255, 255, 0.05);

    .search-field-compact {
      ::v-deep fieldset {
        border-color: rgba(255, 255, 255, 0.12);
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.54);
      }
    }

    .filter-btn-compact.filter-active {
      background-color: rgba(var(--v-primary-base), 0.15);
    }

    // Dark theme filter section styles
    .filter-select {
      ::v-deep fieldset {
        border-color: rgba(255, 255, 255, 0.12);
      }

      ::v-deep .v-label {
        color: rgba(255, 255, 255, 0.7);
      }

      .filter-icon {
        color: rgba(255, 255, 255, 0.54);
      }

      ::v-deep .v-select__selection {
        color: rgba(255, 255, 255, 0.87);
      }
    }
  }
}

// Interactive states for compact header
// Using selector lists to avoid specificity conflicts

// Light theme: Focus states for filter selects
.time-view-header-compact .controls-bar .filter-select ::v-deep .v-input--is-focused {
  fieldset {
    border-color: var(--v-primary-base);
    border-width: 2px;
  }

  .v-label {
    color: var(--v-primary-base);
  }

  .filter-icon {
    color: var(--v-primary-base);
  }
}

// Light theme: Hover states for both search and filter
.time-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset,
.time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

// Dark theme: Focus states for filter selects
.theme--dark .time-view-header-compact .controls-bar .filter-select ::v-deep .v-input--is-focused {
  fieldset {
    border-color: var(--v-primary-base);
    border-width: 2px;
  }

  .v-label {
    color: var(--v-primary-base);
  }

  .filter-icon {
    color: var(--v-primary-base);
  }
}

// Dark theme: Hover states for both search and filter
.theme--dark .time-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset,
.theme--dark .time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

// Responsive adjustments for compact header
@media (max-width: 960px) {
  .time-view-header-compact {
    .header-toolbar {
      height: auto !important;
      min-height: 48px !important;
      max-height: none !important;
    }

    .header-icon-wrapper {
      width: 36px;
      height: 36px;
    }

    .time-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 48px !important;
      }

      ::v-deep .v-tab {
        font-size: 0.813rem;
        min-width: 80px;
        padding: 0 12px;
        height: 48px;
      }
    }

    .add-time-btn-compact {
      height: 36px !important;
      padding: 0 16px !important;
      font-size: 0.875rem;

      .ml-2 {
        margin-left: 4px !important;
      }
    }
  }
}

@media (max-width: 768px) {
  .time-view-header-compact {
    .header-icon-wrapper {
      width: 32px;
      height: 32px;
      margin-right: 8px !important;
    }

    .time-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 44px !important;
      }

      ::v-deep .v-tab {
        font-size: 0.75rem;
        min-width: 70px;
        padding: 0 8px;
        height: 44px;
      }

      ::v-deep .v-tabs-slider {
        height: 2px !important;
      }
    }

    .add-time-btn-compact {
      padding: 0 12px !important;
      min-width: auto !important;
    }

    .header-actions {
      .mr-1, .mr-2 {
        margin-right: 4px !important;
      }

      .action-buttons-container {
        min-width: 250px; // Reserve space for smooth transitions
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
    }

    .controls-bar {
      .search-field-compact {
        ::v-deep .v-input__control {
          min-height: 36px !important;
        }

        ::v-deep .v-input__slot {
          min-height: 36px !important;
        }
      }

      .filter-btn-compact {
        height: 30px !important;
        font-size: 0.813rem;
      }
    }
  }
}

// Dark mode support for search and filter
.theme--dark .search-filter-section {
  .search-field-enhanced {
    ::v-deep .v-input__slot {
      background: rgba(48, 48, 48, 0.9) !important;
      border-color: rgba(255, 255, 255, 0.12);

      &:hover {
        border-color: rgba(25, 118, 210, 0.6);
      }
    }

    ::v-deep .v-input__icon--prepend-inner,
    ::v-deep .v-input__icon--append {
      color: rgba(255, 255, 255, 0.7);
    }

    ::v-deep .v-text-field__slot input::placeholder {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .filter-button-enhanced.filter-active {
    background-color: rgba(25, 118, 210, 0.15) !important;
  }
}

// Responsive adjustments for search and filter bar
@media (max-width: 768px) {
  .search-filter-section {
    .controls-container {
      // Mobile: stack vertically with search+filter first, then date picker
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      .search-filter-row {
        // Keep search and filter side-by-side on mobile
        order: 1;

        .col {
          max-width: 100%;

          &.pl-4 {
            padding-left: 16px;
          }
        }
      }

      .date-picker-row {
        // Date picker comes second and takes full width
        order: 2;

        .col {
          flex-grow: 1;
          max-width: 100%;
        }
      }
    }    .search-field-enhanced {
      ::v-deep .v-input__control {
        min-height: 40px;
        max-height: 40px;
      }
    }

    .filter-button-enhanced {
      height: 34px !important;
      min-width: 100px;
      font-size: 0.875rem;
    }
  }
}

@media (max-width: 1024px) {
  .search-filter-section {
    .filter-button-enhanced {
      min-width: 100px;

      span {
        display: none;
      }

      // Show only icon on tablet sizes
      .fa-filter {
        margin-right: 0 !important;
      }
    }
  }
}// Responsive adjustments for summary cards
@media (max-width: 768px) {
  .summary-cards-container {
    .summary-card {
      .summary-value {
        font-size: 1.5rem !important;
      }
    }
  }

  .search-filter-section {
    .v-col {
      margin-bottom: 8px;
    }
  }

  // Card actions responsive adjustments for mobile
  .time-entry-card .v-card__actions,
  .time-entry-card-compact .v-card__actions {
    padding: 8px 12px;

    .v-btn.v-btn--icon {
      // Slightly larger touch targets on mobile
      min-width: 48px;
      min-height: 48px;
    }

    // Adjust spacing between action buttons on mobile
    .d-flex.align-center {
      gap: 8px;
    }
  }

  // Responsive styling for column calendar view on smaller screens
  .time-entries-columns {
    .col {
      min-width: 120px; // Slightly smaller minimum width on mobile
    }

    .day-column-header {
      .v-card__title {
        padding: 8px !important; // Reduce padding
      }

      .day-column-date {
        font-size: 0.65rem; // Slightly smaller text
      }

      .v-chip {
        font-size: 0.7rem; // Smaller chip text
        height: 22px; // Smaller chip height
      }
    }

    .day-column-entries {
      padding: 2px 4px; // Less padding on mobile
    }

    .time-entry-card-compact {
      .card-header-compact {
        padding: 4px 6px !important; // More compact padding
      }

      .entry-info-row {
        gap: 4px; // Reduce gap between icon and text

        .icon-fixed-width {
          width: 12px; // Smaller icons
        }
      }
    }
  }
}

// Global icon selectors (lower specificity, must come before specific filter icon selectors)
.v-data-table .v-icon {
  font-size: 18px;
}

.filter-menu {
  max-width: 360px;

  .v-card-title {
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .v-card-text {
    padding-top: 16px;
  }

  .v-select {
    .v-icon {
      opacity: 0.6;
    }
  }
}

// Filter select dropdown icon styles (higher specificity, must come after global icon selectors)
.time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__append-inner {
  margin-top: 10px !important;

  .v-icon {
    font-size: 20px !important;
    color: rgba(0, 0, 0, 0.54);
  }
}

.theme--dark .time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__append-inner .v-icon {
  color: rgba(255, 255, 255, 0.54);
}

// Week container styling
.week-container {
  width: 100%;

  .container {
    max-width: 100%;
  }

  // Locked week warning cards - softer background
  .v-card.warning {
    background-color: #fff6ea !important;
    border-color: #f0c674 !important;

    .v-card__text {
      color: #8b6914 !important; // Darker text for better contrast
    }
  }

  // Week navigation group styling
  .week-navigation-group {
    .v-btn {
      transition: all 0.2s ease;
    }

    .white-space-nowrap {
      white-space: nowrap;
    }
  }
}

.tab-button {
  min-width: 120px;
}

// View Mode Toggle Styles - Must come after .week-container for specificity ordering
.v-btn-toggle.v-btn-toggle--borderless {
  .v-btn {
    border-radius: 0 !important;
  }
}

// More specific pseudo-class selectors must come after base .v-btn
.v-btn-toggle.v-btn-toggle--borderless .v-btn:first-child {
  border-radius: 4px 0 0 4px !important;
}

.v-btn-toggle.v-btn-toggle--borderless .v-btn:last-child {
  border-radius: 0 4px 4px 0 !important;
}

// Select column styling
::v-deep .select-column {
  width: 50px !important;
  min-width: 50px !important;
  max-width: 50px !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}

// Stacked Progress Bar Styling
.time-breakdown-card {
  border-radius: 8px !important;

  .time-breakdown-title {
    color: #055289; // darkestblue for light mode
  }
}

.stacked-progress-container {
  position: relative;
}

.stacked-progress-bar {
  display: flex;
  width: 100%;
  height: 24px;
  border-radius: 16px;
  overflow: hidden;
  background-color: #f5f5f5; // Light gray background for empty state
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
}

// Dark mode for Time Breakdown (must come after base styles)
.theme--dark .time-breakdown-card {
  .time-breakdown-title {
    color: #90caf9 !important; // Light blue with good contrast on dark background
  }

  .stacked-progress-bar {
    background-color: #424242 !important; // Darker gray for dark mode empty state
  }
}

.progress-segment {
  height: 100%;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    filter: brightness(1.1);
    z-index: 1;
  }

  &:first-child {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }

  &:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  // Subtle separator between segments
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 10%;
    height: 80%;
    width: 1px;
    background: rgba(255, 255, 255, 0.3);
  }
}

// Color classes for each time type - Monochromatic scheme using hydrogen.js colors
.segment-billable {
  background: linear-gradient(135deg, #055289 0%, #0062a8 100%); // darkestblue -> darkblue
}

.segment-project {
  background: linear-gradient(135deg, #007dd6 0%, #0094ff 100%); // blue -> brightblue
}

.segment-nonbillable {
  background: linear-gradient(135deg, #0094ff 0%, #00b6ff 100%); // brightblue -> babyblue
}

.segment-adx {
  background: linear-gradient(135deg, #647b8d 0%, #a5b1b9 100%); // gray -> midgray
}

.segment-adxpg {
  background: linear-gradient(135deg, #5e6769 0%, #647b8d 100%); // coolgray -> gray
}

// Legend styling
.legend-container,
.legend-container-inline {
  gap: 4px;

  .legend-chip {
    font-size: 11px !important;
    height: 22px !important;
    padding: 0 8px !important;

    &.segment-billable {
      background: #055289 !important; // darkestblue
    }

    &.segment-project {
      background: #007dd6 !important; // blue
    }

    &.segment-nonbillable {
      background: #0094ff !important; // brightblue
    }

    &.segment-adx {
      background: #647b8d !important; // gray
    }

    &.segment-adxpg {
      background: #5e6769 !important; // coolgray
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .stacked-progress-bar {
    height: 28px;
    border-radius: 14px;
  }

  .legend-container,
  .legend-container-inline {
    .legend-chip {
      font-size: 10px !important;
      height: 20px !important;
    }
  }
}

// Tablet and smaller - stack legend below on smaller screens
@media (max-width: 960px) {
  .time-breakdown-card {
    .align-center {
      flex-direction: column;
      align-items: flex-start !important;

      .col-auto:last-child {
        width: 100%;
        margin-top: 8px;

        .legend-container-inline {
          justify-content: flex-start !important;
        }
      }
    }
  }
}

// Dark mode for locked week warnings
.theme--dark .week-container {
  .v-card.warning {
    background-color: #4a3f2a !important; // Darker warm background for dark mode
    border-color: #8b7a4f !important;

    .v-card__text {
      color: #f0c674 !important; // Lighter text for dark mode
    }
  }
}

// Dark mode card styling
.theme--dark .time-entry-card {
  background-color: #2d2d2d;
  border-color: rgba(255, 255, 255, 0.12);

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    border-color: rgba(var(--v-primary-base), 0.5);
  }

  &.entry-selected {
    background-color: rgba(var(--v-primary-base), 0.15);
  }

  &.entry-locked {
    opacity: 0.6;
    background-color: rgba(255, 255, 255, 0.05);
  }

  .v-card__title {
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent);
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .entry-date-badge {
    .day-of-week {
      color: rgba(255, 255, 255, 0.6);
    }

    .short-date {
      color: rgba(255, 255, 255, 0.87);
    }
  }

  // Dark mode for icon-based info rows
  .entry-info-row {
    .entry-info-content {
      color: rgba(255, 255, 255, 0.87);
    }
  }

  // Dark mode for notes display
  .entry-notes-display {
    background-color: rgba(255, 255, 255, 0.05);
    border-left-color: rgba(var(--v-primary-base), 0.5);

    .work-notes-text {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .entry-detail {
    .detail-label {
      color: #b0bec5 !important; // Lighter gray for better contrast in dark mode
    }

    .detail-value {
      color: rgba(255, 255, 255, 0.87);

      &.work-notes-text {
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }

  .v-card__actions {
    background: rgba(255, 255, 255, 0.05);
    border-top-color: rgba(255, 255, 255, 0.12);

    // Dark mode subtle action button colors (light blue-grey)
    .action-btn {
      color: $color-monochrome-actiongraylite !important;
    }
  }

  // Make outlined time chips more visible in dark mode
  .time-type-chip-header,
  .time-type-chip-compact {
    border-width: 2px !important; // Slightly thicker border for better visibility

    // Lighten the colors for better contrast in dark mode
    &.v-chip--outlined {
      filter: brightness(1.4) saturate(0.9);
    }
  }
}

// Dark mode for Column Calendar View
.theme--dark .time-entries-columns {
  > .col:not(:last-child) {
    border-right-color: rgba(255, 255, 255, 0.12);
  }

  .day-column-header {
    background-color: #2d2d2d;
    border-color: rgba(255, 255, 255, 0.12);

    .v-card__title {
      background: linear-gradient(to bottom, rgba(var(--v-primary-base), 0.15), rgba(var(--v-primary-base), 0.05));
    }

    // Day column date styling for dark mode
    .day-column-date {
      color: #90caf9 !important; // Light blue with good contrast

      &.locked {
        color: #b0bec5 !important; // Lighter gray for locked dates in dark mode
      }
    }
  }

  .time-entry-card-compact {
    background-color: #2d2d2d;
    border-color: rgba(255, 255, 255, 0.12);

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
      border-color: rgba(var(--v-primary-base), 0.5);
    }

    &.entry-selected {
      background-color: rgba(var(--v-primary-base), 0.15);
    }

    .entry-info-row {
      .entry-info-content {
        color: rgba(255, 255, 255, 0.87);
      }
    }

    .entry-notes-display {
      background-color: rgba(255, 255, 255, 0.05);

      .work-notes-text {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .entry-detail-compact .detail-value {
      color: rgba(255, 255, 255, 0.87);

      &.work-notes-text {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .entry-detail-compact .detail-label {
      color: #b0bec5 !important; // Lighter gray for better contrast in dark mode
    }
  }
}

// Dark mode for empty state text
.theme--dark .empty-state-text {
  color: #b0bec5 !important; // Lighter gray for better contrast in dark mode
}

// Clickable empty state styling
.empty-state-clickable {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent !important;

  // Override Vuetify's default hover shadow
  &::before {
    display: none !important;
  }

  // Override Vuetify's focus state box-shadow
  &.v-sheet.v-card--hover:focus {
    box-shadow: none !important;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(25, 118, 210, 0.12) !important;
    background-color: rgba(25, 118, 210, 0.04) !important;
    border-color: rgba(25, 118, 210, 0.2) !important;

    .coolgray--text {
      color: var(--v-primary-base) !important;
      transition: color 0.3s ease;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 8px 24px rgba(25, 118, 210, 0.12) !important;
    background-color: rgba(25, 118, 210, 0.04) !important;
    border-color: rgba(25, 118, 210, 0.2) !important;
  }

  &:active {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
  }
}

// Dark mode specific styling
.theme--dark .empty-state-clickable {
  &.v-sheet.v-card--hover:focus {
    box-shadow: none !important;
  }

  &:hover {
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.08) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;

    .coolgray--text {
      color: rgba(255, 255, 255, 0.9) !important;
    }
  }

  &:focus {
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.08) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }
}

// ============================================================================
// HOVER STATES - Must come LAST to maintain proper CSS specificity order
// All :hover pseudo-class selectors must come after all base button selectors
// ============================================================================

.v-data-table .entry-actions .v-btn:hover {
  transform: scale(1.1);
}

.week-container .week-navigation-group .v-btn:hover {
  transform: scale(1.1);
}

.time-entry-card .v-card__actions .v-btn:hover {
  transform: scale(1.15);
}

// ============================================================================
// FOCUS STATES - Accessibility keyboard navigation states
// Must come before dark theme hovers for proper CSS specificity ordering
// ============================================================================

.time-entry-card .v-card__actions .v-btn:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

.theme--dark .time-entry-card .v-card__actions .v-btn:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

// Dark mode hovers - Most specific, comes last
.theme--dark .v-data-table .entry-actions .v-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

// Dark mode action button hover states - Most specific, comes last
.theme--dark .time-entry-card .v-card__actions .action-btn.action-btn-duplicate:hover:not(:disabled) {
  color: #81c784 !important; // Light green on hover
}

.theme--dark .time-entry-card .v-card__actions .action-btn.action-btn-edit:hover:not(:disabled) {
  color: #64b5f6 !important; // Light blue on hover
}

.theme--dark .time-entry-card .v-card__actions .action-btn.action-btn-delete:hover:not(:disabled) {
  color: #e57373 !important; // Light red on hover
}
</style>
